/**
 * /video-retouch — страница ретуши лица на видео.
 *
 * Устройство:
 *   landmarks / engine / smooth / features / tone — то же ядро, что у фото
 *   video-engine.js         — параметры сцены и покадровая обработка
 *   canvas-video-export.js  — аппаратный энкодер (общий примитив)
 *   этот файл               — плеер, предпросмотр, экспорт, кап, телеметрия
 *
 * ЧЕТЫРЕ РЕШЕНИЯ, КОТОРЫЕ СТОИТ ОБЪЯСНИТЬ
 *
 * 1. ПЕРЕМОТКА НЕ ЖДЁТ `requestVideoFrameCallback`. Это была самая дорогая
 *    строка тула. rVFC срабатывает, когда браузер ПОКАЗЫВАЕТ кадр, а у видео
 *    на ПАУЗЕ показа не происходит — колбэк просто не приходил, и каждый кадр
 *    досиживал страховочные 300 мс. Замер (Chrome 152, 1280×720):
 *
 *        ожидание rVFC   304 мс на кадр
 *        только onseeked  23 мс на кадр      ← и кадр ТОТ ЖЕ
 *
 *    Совпадение проверено побайтово: 25 позиций, 25 одинаковых хэшей,
 *    25 разных кадров (tests/playwright/video-retouch-seek-is-not-a-sleep.mjs).
 *    На десятисекундном ролике 30 к/с это 90 секунд чистого сна, то есть
 *    больше половины всего экспорта.
 *
 * 2. ЦЕНА НАЗЫВАЕТСЯ ДО НАЖАТИЯ И ВИДНА ВО ВРЕМЯ. Обработка покадровая:
 *    на этой машине ~6–7 секунд работы на секунду видео. Такое ожидание
 *    нельзя начинать молча, поэтому под кнопкой всегда написано, сколько
 *    кадров и сколько примерно минут, а на кадре во время разбора висит
 *    вуаль с полосой. Молчащий тул и был жалобой, с которой началась эта
 *    переделка.
 *
 * 3. ФРАГМЕНТ — ЧАСТЬ ИНСТРУМЕНТА, А НЕ УКРАШЕНИЕ. Сорокаминутный вебинар
 *    целиком в браузере не обработать никогда; двадцать секунд из него —
 *    за пару минут. Поэтому ручки «от» и «до» стоят на дорожке плеера, а
 *    отказ по длине считается по ВЫБРАННОМУ фрагменту, а не по файлу.
 *
 * 4. ПРЕДПРОСМОТР — СТОП-КАДР, НО ЕСТЬ «ПРОВЕРИТЬ В ДВИЖЕНИИ». Живьём кадр
 *    стоит сотни миллисекунд, и «играть с эффектом» честно нельзя. Зато
 *    можно собрать короткий отрезок в память и крутить его по кругу — так
 *    делают монтажные программы (RAM preview), и именно так видно главное
 *    обещание тула: что эффект не дрожит между кадрами.
 */
(function () {
    'use strict';
    // window.__perPixel / window.__setText (timbrica-core.js) write a position or a text only when what is
    // shown changes. A page built before they existed can still be handed this file — it then writes the
    // plain way. Why: docs/why/primitives/per-pixel-writes.md.
    function textWrite(e, t) { if (window.__setText) return window.__setText(e, t); e.textContent = t; }


    var CFG = window.VR_CFG || {};
    var SLUG = CFG.slug || 'video-retouch';
    var I = CFG.i18n || {};

    /* Потолок на ВЫБРАННЫЙ ФРАГМЕНТ, а не на файл: длинный ролик теперь не
       отвергается — из него берут кусок. */
    var MAX_RANGE = 600;
    /* Длинная сторона холста предпросмотра. Совпадает с экспортом, пока тот не
       выше 1080p: тогда «крупно» показывает настоящий пиксель выдачи. Выше —
       ограничиваем: кадр 4K стоит втрое дороже, а предпросмотр обязан
       отзываться на ползунок. */
    var PREVIEW_MAX_H = 1080;
    var SEEK_DEADLINE_MS = 2500;
    /* Проверка в движении: сколько секунд собирать и с какой частотой. 2.5 с
       при 15 к/с — 38 кадров, около десяти секунд сборки на этой машине.
       Больше нельзя: это ожидание внутри ожидания. */
    var MOTION_SEC = 2.5;
    var MOTION_FPS = 15;

    var el = {};
    var st = {
        file: null, url: '', limitKey: null, scene: null, proc: null,
        dur: 0, vw: 0, vh: 0, srcFps: 0,
        outW: 0, outH: 0,              // холст предпросмотра
        expW: 0, expH: 0,              // разрешение выдачи
        inT: 0, outT: 0,
        busy: false, pending: false, wipe: 0.5, abort: null, ready: false,
        spots: [], aim: false, lastMainFace: null,
        canExport: undefined, exportWhy: '',
        seekMisses: 0, seekTotal: 0, msPerFrame: 0, msPerSeek: 0,
        cmp: 'wipe', zoom: false, playing: false, exporting: false,
        veiled: false, building: false,
        motion: null,                  // {frames:[{before,after}], fps, from, idx, playing, raf}
        t0: 0,
    };

    function $(id) { return document.getElementById(id); }
    function ae(name, data) { try { window.__ae && window.__ae(name, Object.assign({ slug: SLUG }, data || {})); } catch (_) { } }
    var _stages = {};
    function stageOnce(name, data) {
        if (_stages[name]) return;
        _stages[name] = 1;
        ae('tool_funnel', Object.assign({ f: name }, data || {}));
    }
    function fmt(s) {
        s = Math.max(0, Math.round(s));
        return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
    }
    /** Человеческая длительность ожидания: «40 с», «3 мин», «1 ч 10 мин». */
    function human(sec) {
        sec = Math.max(1, Math.round(sec));
        if (sec < 90) return sec + ' ' + (I.sec_unit || 's');
        var m = Math.round(sec / 60);
        if (m < 90) return m + ' ' + (I.min_unit || 'min');
        var h = Math.floor(m / 60);
        return h + ' ' + (I.hour_unit || 'h') + ' ' + (m % 60) + ' ' + (I.min_unit || 'min');
    }

    // ════════════════════════════════════════════════════════════════════════
    //  Загрузка страницы
    // ════════════════════════════════════════════════════════════════════════
    document.addEventListener('DOMContentLoaded', function () {
        el.work = $('vrWork'); el.video = $('vrVideo');
        el.before = $('vrBefore'); el.after = $('vrAfter'); el.box = $('vrBox');
        el.explain = $('vrExplain'); el.legend = $('vrLegend');
        el.spotOn = $('vrSpotOn'); el.spotUndo = $('vrSpotUndo'); el.spotClear = $('vrSpotClear');
        el.spotSize = $('vrSpotSize'); el.spotSizeOut = $('vrSpotSizeOut'); el.aimRing = $('vrAimRing');
        el.paneSpot = $('vrPaneSpot'); el.hint = $('vrHint'); el.tabs = $('vrTabs');
        el.wipe = $('vrWipe'); el.truth = $('vrTruth'); el.badge = $('vrBadge');
        el.veil = $('vrVeil'); el.veilTxt = $('vrVeilTxt'); el.veilFill = $('vrVeilFill');
        el.veilSub = $('vrVeilSub'); el.veilStop = $('vrVeilStop');
        el.play = $('vrPlay'); el.track = $('vrTrack'); el.sel = $('vrSel');
        el.inH = $('vrIn'); el.outH = $('vrOut'); el.head = $('vrHead');
        el.timeCur = $('vrTimeCur'); el.timeDur = $('vrTimeDur');
        el.cmp = $('vrCmp'); el.motionBtn = $('vrMotion'); el.zoomBtn = $('vrZoom');
        el.exportBtn = $('vrExport'); el.cancelBtn = $('vrCancel');
        el.prog = $('vrProg'); el.bar = $('vrBarFill'); el.stage = $('vrProgStage'); el.eta = $('vrProgEta');
        el.plan = $('vrPlan'); el.src = $('vrSrc'); el.fail = $('vrFail'); el.audio = $('vrAudio');
        el.height = $('vrHeight'); el.format = $('vrFormat');
        el.teethState = $('vrTeethState');
        el.mainFace = $('vrMainFace'); el.reset = $('vrReset');
        el.sl = {
            shine: $('vrShine'), smooth: $('vrSmooth'), red: $('vrRed'), eye: $('vrEye'),
            teeth: $('vrTeeth'), sclera: $('vrSclera'), exposure: $('vrExposure'), warmth: $('vrWarmth'),
        };
        el.out = {
            shine: $('vrShineOut'), smooth: $('vrSmoothOut'), red: $('vrRedOut'), eye: $('vrEyeOut'),
            teeth: $('vrTeethOut'), sclera: $('vrScleraOut'), exposure: $('vrExposureOut'), warmth: $('vrWarmthOut'),
        };
        if (!el.work) return;

        // Через `__bindUploaderFile`, а не голым addEventListener: файл может
        // быть выбран РАНЬШЕ, чем отложенный скрипт тула подписался, и тогда
        // событие уходит в пустоту (сторож — UploaderListenerRaceTest).
        var zone = $('vrUpload');
        var onFile = function (file, detail) {
            var f = file || (detail && detail.files && detail.files[0]);
            if (f) load(f);
        };
        if (typeof window.__bindUploaderFile === 'function') {
            window.__bindUploaderFile(zone, onFile);
        } else if (zone) {
            zone.addEventListener('tool-uploader:file', function (e) { onFile(e.detail && e.detail.file, e.detail); });
        }
        document.addEventListener('tool-uploader:clear', reset);

        bindSliders(); bindPresets(); syncPresetHighlight(); bindWipe(); bindPlayer(); bindCompare(); bindSpots();
        el.exportBtn && el.exportBtn.addEventListener('click', onExport);
        el.cancelBtn && el.cancelBtn.addEventListener('click', function () { st.abort && st.abort.abort(); });
        el.veilStop && el.veilStop.addEventListener('click', function () { st.abort && st.abort.abort(); });
        el.motionBtn && el.motionBtn.addEventListener('click', onMotion);
        el.zoomBtn && el.zoomBtn.addEventListener('click', toggleZoom);
        el.reset && el.reset.addEventListener('click', function () { applyPreset('natural'); });
        el.mainFace && el.mainFace.addEventListener('change', function () {
            window.__toolSettings && window.__toolSettings.save(SLUG, 'mainFace', el.mainFace.checked ? '1' : '0');
            ae('tool_interact', { f: 'main_face', on: el.mainFace.checked ? 1 : 0 });
            schedulePreview(0);
        });
        el.height && el.height.addEventListener('change', function () {
            resizeCanvases();
            schedulePreview(0);
            updatePlan();
        });
        el.format && el.format.addEventListener('change', updatePlan);
        try {
            if (window.__toolSettings && el.mainFace) {
                el.mainFace.checked = window.__toolSettings.load(SLUG, 'mainFace', '0') === '1';
            }
        } catch (_) { }

        // Уход со страницы не должен оставлять крутящуюся петлю и играющее видео.
        window.addEventListener('pagehide', stopEverything);
        document.addEventListener('visibilitychange', function () { if (document.hidden) stopEverything(); });
    });

    function stopEverything() {
        stopMotion();
        if (el.video && !el.video.paused) { try { el.video.pause(); } catch (_) { } }
        setPlayingUI(false);
    }

    /**
     * Отказ открыть файл — НЕ тост.
     *
     * Тост живёт секунды и уходит, а загрузчик к этому моменту уже свернулся в
     * строку «имя файла · Другой файл»: со стороны выглядит, будто файл принят
     * и тул просто ничего не делает. Поэтому причина остаётся на экране, пока
     * не выберут другой файл, и называет ВЫХОД, а не только беду.
     */
    /**
     * @param {false|'open'|'detector'} kind
     *   'open'     — браузер не открыл файл (кодек), с ссылкой на конвертер;
     *   'detector' — распознавание лица не запустилось в этом браузере.
     *     ⚠️ Это НЕ «лицо не найдено» и не тост. Жалоба Левона 05.09: wasm
     *     детектора падал с «Aborted()», тост с этим текстом жил секунды, а
     *     ползунки оставались живыми — человек двигал их четверть часа и
     *     писал «изменение не замечаю, режим разница не видит разницу».
     *     Причина обязана стоять на экране, пока не выберут другой файл,
     *     а органы, которым нечем работать, — быть погашены.
     */
    function showFail(kind) {
        if (!el.fail) return;
        el.fail.hidden = !kind;
        if (!kind) return;
        while (el.fail.firstChild) el.fail.removeChild(el.fail.firstChild);
        if (kind === 'detector') {
            el.fail.appendChild(document.createTextNode(I.fail_detector || I.no_face || ''));
            return;
        }
        el.fail.appendChild(document.createTextNode((I.fail_open || '') + ' '));
        var url = CFG.converterUrl;
        if (url && I.fail_convert) {
            var a = document.createElement('a');
            a.href = url;
            a.textContent = I.fail_convert;
            el.fail.appendChild(a);
        }
    }

    /**
     * Погасить или вернуть рейку настроек. Без детектора ретуши нет, и живой
     * ползунок обещает работу, которой не будет, — это ровно то, что видел
     * Левон. `disabled` на органах + класс на корне для стиля.
     */
    function setRailEnabled(on) {
        try {
            var rail = document.querySelector('.vr-app .tc-rail');
            if (rail) {
                var ctl = rail.querySelectorAll('input, button, select');
                for (var i = 0; i < ctl.length; i++) ctl[i].disabled = !on;
            }
            var app = document.querySelector('.vr-app');
            if (app) app.classList.toggle('vr-nodetector', !on);
            var tools = document.getElementById('vrMotion');
            if (tools) tools.disabled = !on || !st.ready;
        } catch (_) { }
    }

    function reset() {
        stopEverything();
        showFail(false);
        setRailEnabled(true);
        st.modelFailed = false;
        st.ready = false; st.scene = null; st.proc = null; st.file = null;
        st.msPerFrame = 0; st.canExport = undefined; st.lastOut = null;
        clearSpots();
        if (st.url) { try { URL.revokeObjectURL(st.url); } catch (_) { } st.url = ''; }
        el.work.hidden = true;
        setProgress(false);
        veil(false);
    }

    // ════════════════════════════════════════════════════════════════════════
    //  Загрузка ролика
    // ════════════════════════════════════════════════════════════════════════
    async function load(file) {
        stopEverything();
        showFail(false);
        // Пятна привязаны к лицу ЭТОГО ролика — у нового они ничего не значат.
        clearSpots();
        st.file = file;
        st.lastOut = null;
        st.t0 = performance.now();
        _stages = {};
        try {
            st.limitKey = (window.__toolLimit && window.__toolLimit.fileKey)
                ? window.__toolLimit.fileKey(file) : null;
        } catch (_) { st.limitKey = null; }

        if (st.url) { try { URL.revokeObjectURL(st.url); } catch (_) { } }
        st.url = URL.createObjectURL(file);
        el.video.muted = true;
        el.video.src = st.url;
        try {
            await new Promise(function (res, rej) {
                el.video.onloadedmetadata = res;
                el.video.onerror = function () { rej(new Error('decode')); };
            });
        } catch (err) {
            window.__toast && window.__toast(I.err_read || 'error', 'error');
            showFail(true);
            ae('tool_error', { f: 'decode_failed' });
            return;
        }

        st.dur = el.video.duration || 0;
        st.vw = el.video.videoWidth; st.vh = el.video.videoHeight;
        if (!st.vw || !st.vh || !isFinite(st.dur) || st.dur <= 0) {
            window.__toast && window.__toast(I.err_read || 'error', 'error');
            showFail(true);
            ae('tool_error', { f: 'no_dims' });
            return;
        }
        stageOnce('picked', { mb: Math.round((file.size || 0) / 1048576) });

        st.inT = 0;
        st.outT = Math.min(st.dur, MAX_RANGE);
        el.timeDur.textContent = fmt(st.dur);
        el.work.hidden = false;

        // Показать первый кадр НЕМЕДЛЕННО: до всякого разбора человек должен
        // увидеть свой ролик, а не пустой прямоугольник.
        buildHeights();
        resizeCanvases();
        setWipe(0.5);
        drawRange();

        // ── МОЖЕТ ЛИ ЭТОТ БРАУЗЕР ВООБЩЕ СОБРАТЬ ВИДЕО ─────────────────────
        // Спросить надо СЕЙЧАС, а не после того, как человек подождал разбор,
        // покрутил ползунки и нажал «Сохранить»: на Safari до 17 WebCodecs нет
        // вовсе, и там его встречал бы общий отказ «не удалось сохранить».
        checkExportSupport();

        veil(true, I.loading || '', 0.04, '');
        await seek(Math.min(0.1, st.dur / 2));
        drawBefore();

        st.srcFps = await probeFps();
        await readScene();
        if (st.ready) await renderPreview();
        updatePlan();
        ae('tool_funnel', {
            f: 'file_ready', w: st.vw, h: st.vh, sec: Math.round(st.dur), fps: st.srcFps,
            ms: Math.round(performance.now() - st.t0),
        });
    }

    /**
     * Частота кадров ИСХОДНИКА.
     *
     * Раньше экспорт всегда собирался на 25 к/с. Для ролика 30 или 60 это не
     * «настройка», а порча: кадры прореживаются, движение начинает дёргаться,
     * а длительность выдачи расходится со звуком. Спросить контейнер дёшево —
     * `requestVideoFrameCallback` отдаёт `mediaTime` каждого показанного кадра,
     * и медиана разностей и есть частота. Ответ приводится к ближайшей
     * стандартной, иначе 29.97 приезжает как 30.03.
     */
    async function probeFps() {
        // 1. КОНТЕЙНЕР. Единственный источник, который знает настоящую
        //    частоту: остальные меряют показанные кадры, а браузер выбрасывает
        //    дубликаты — ролик 25 к/с из неподвижной картинки «измерялся» как
        //    12 к/с, и выдача выходила вдвое медленнее исходника.
        try {
            if (window.WebCodecsFrameExtractor && st.file) {
                var cap = await window.WebCodecsFrameExtractor.isSupported(st.file);
                st.wcCap = cap;   // тот же ответ решает, доступен ли конвейерный экспорт (pickLane)
                if (cap && typeof cap.fps === 'number' && cap.fps > 1 && cap.fps <= 240) {
                    return Math.round(cap.fps * 1000) / 1000;
                }
            }
        } catch (_) { }
        return measureFps();
    }

    /** Запасной замер по показанным кадрам — для контейнеров, которых mp4box не читает. */
    function measureFps() {
        var STD = [23.976, 24, 25, 29.97, 30, 48, 50, 59.94, 60, 120];
        return new Promise(function (res) {
            var v = el.video;
            if (typeof v.requestVideoFrameCallback !== 'function') return res(30);
            var times = [], done = false;
            var fin = function () {
                if (done) return; done = true;
                try { v.pause(); } catch (_) { }
                var d = [];
                for (var i = 1; i < times.length; i++) { var x = times[i] - times[i - 1]; if (x > 0.0005) d.push(x); }
                if (d.length < 3) return res(30);
                d.sort(function (a, b) { return a - b; });
                // МИНИМАЛЬНЫЙ промежуток, а не медиана: показ кадров можно
                // пропустить (тогда промежуток больше настоящего), но нельзя
                // показать два кадра ближе, чем длится один.
                var raw = 1 / d[0];
                var best = 30, bd = 1e9;
                for (var k = 0; k < STD.length; k++) {
                    var diff = Math.abs(STD[k] - raw);
                    if (diff < bd) { bd = diff; best = STD[k]; }
                }
                res(bd < raw * 0.08 ? best : Math.max(5, Math.min(120, Math.round(raw))));
            };
            var tick = function (now, meta) {
                times.push(meta && meta.mediaTime !== undefined ? meta.mediaTime : v.currentTime);
                if (times.length >= 16) fin(); else v.requestVideoFrameCallback(tick);
            };
            setTimeout(fin, 1400);
            v.muted = true;
            var p = v.play();
            if (p && p.then) p.then(function () { v.requestVideoFrameCallback(tick); }).catch(fin);
            else v.requestVideoFrameCallback(tick);
        });
    }

    async function checkExportSupport() {
        st.canExport = true;
        try {
            if (!window.CanvasVideoExport || typeof VideoEncoder === 'undefined') {
                st.canExport = false; st.exportWhy = 'no-webcodecs';
            } else {
                var sup = await window.CanvasVideoExport.isSupported({ width: 640, height: 360, fps: 25, format: 'mp4' });
                if (!sup || !sup.ok) { st.canExport = false; st.exportWhy = (sup && sup.reason) || 'unsupported'; }
            }
        } catch (e) { st.canExport = false; st.exportWhy = 'probe-failed'; }
        if (!st.canExport) {
            setProgress(false);
            showTruth();
            ae('tool_blocked', { f: 'no_export_support', why: st.exportWhy });
        }
    }

    // ════════════════════════════════════════════════════════════════════════
    //  Разрешение выдачи
    // ════════════════════════════════════════════════════════════════════════
    /**
     * Список высот строится из ИСХОДНИКА: предлагать 1080p для ролика 720p —
     * это продавать растяжку. Потолок тира закрывает лишние варианты сам
     * (public/js/quality-tier.js, ось «height»), поэтому здесь никакой логики
     * тарифов нет и быть не должно.
     *
     * До этой правки экспорт молча уменьшал ЛЮБОЙ ролик до 1280 по длинной
     * стороне — то есть Премиум платил и получал ровно то же, что гость, а
     * материал 1080p терял половину пикселей без единого слова.
     */
    function buildHeights() {
        if (!el.height) return;
        var opts = [], seen = {};
        var steps = [2160, 1440, 1080, 720, 480, 360];
        // ⚠️ Разрешение считается по КОРОТКОЙ стороне, а не по высоте.
        // «720p» у вертикального ролика — это 720×1280, так его называют и так
        // его отдаёт телефон. Потолок, применённый к ВЫСОТЕ, превращал
        // вертикальные 720×1280 в 405×720, то есть в «405p», и человек получал
        // втрое меньше пикселей, чем обещает надпись. Ось тира от этого не
        // меняется: сравнение идёт с тем же `video_max_height`, просто число,
        // которое мы в неё подставляем, теперь означает то же, что на вывеске.
        var src = Math.min(st.vw, st.vh);
        if (!seen[src]) { opts.push(src); seen[src] = 1; }
        for (var i = 0; i < steps.length; i++) {
            if (steps[i] < src && !seen[steps[i]]) { opts.push(steps[i]); seen[steps[i]] = 1; }
        }
        opts.sort(function (a, b) { return b - a; });
        el.height.innerHTML = '';
        for (var k = 0; k < opts.length; k++) {
            var o = document.createElement('option');
            o.value = String(opts[k]);
            // Текст варианта — короткий: список стоит в узкой колонке, а длина
            // <option> задаёт полю min-content и умеет раздвинуть страницу вбок.
            // Про исходник сказано ОТДЕЛЬНОЙ строкой под полями.
            o.textContent = opts[k] + 'p';
            el.height.appendChild(o);
        }
        el.height.value = String(src);
        // Гейт может закрыть исходное разрешение: тогда он сам переставит выбор
        // на ближайший открытый вариант, и `exportSize()` прочитает его из
        // списка — потому и не кэшируется нигде.
        try { window.__qualityTier && window.__qualityTier.gate(el.height, 'height'); } catch (_) { }
    }

    /**
     * ⚠️ ВЫБОР читается ТОЛЬКО из списка, и нигде не кэшируется.
     * Одна переменная под «что выбрал человек» и «какая высота получилась»
     * ломается на вертикальном ролике: там высота — ДЛИННАЯ сторона, она
     * возвращалась в качестве выбора, зажималась короткой стороной исходника
     * и молча отменяла выбор «480p». Симптом — «поставил меньше, а файл
     * прежний», причина — совмещённое хранилище.
     */
    function exportSize() {
        var want = parseInt(el.height && el.height.value, 10) || Math.min(st.vw, st.vh);
        // Страховка: даже если разметка гейта не отработала, выше потолка тира
        // выдачи не будет. Показываем это ВСЕГДА (строка плана), а не молча.
        try {
            if (window.__qualityTier) want = window.__qualityTier.clamp('height', want);
        } catch (_) { }
        var srcShort = Math.min(st.vw, st.vh);
        want = Math.min(want, srcShort);
        var k = want / srcShort;
        return {
            w: Math.max(2, Math.round(st.vw * k / 2) * 2),
            h: Math.max(2, Math.round(st.vh * k / 2) * 2),
        };
    }

    function resizeCanvases() {
        var e = exportSize();
        st.expW = e.w; st.expH = e.h;
        var k = Math.min(1, PREVIEW_MAX_H / Math.min(e.w, e.h));
        st.outW = Math.max(2, Math.round(e.w * k));
        st.outH = Math.max(2, Math.round(e.h * k));
        el.before.width = st.outW; el.before.height = st.outH;
        el.after.width = st.outW; el.after.height = st.outH;
        // Коробка держит пропорции ролика: без этого холст растягивается по
        // ширине, упирается в max-height и object-fit дорисовывает чёрные поля
        // по бокам — выглядит как брак кадрирования. Ролик НЕ обрезается ни в
        // одной ориентации: `object-fit:contain` показывает его целиком.
        var portrait = st.outH > st.outW;
        // Вертикальному отдаём больше высоты: он и так узкий, а панель под него
        // всё равно занимает всю ширину колонки.
        // Потолок кадра берётся из переменной, которую считает страница по
        // ОСТАТКУ места (см. vrFit в блейде): доля окна ничего не знает про
        // плёнку, ряд режимов и карточку выдачи под кадром, и на коротком
        // экране кнопка «Сохранить видео» уезжала за нижний край. Запасное
        // значение — прежняя доля окна, если переменной почему-то нет.
        var cap = portrait
            ? 'min(var(--vr-frame-max, 64dvh), 620px)'
            : 'min(var(--vr-frame-max, 52dvh), 520px)';
        var ratio = st.outW / st.outH;
        el.box.style.aspectRatio = st.outW + ' / ' + st.outH;
        el.box.style.maxHeight = cap;
        el.box.style.width = 'min(100%, calc(' + cap + ' * ' + ratio + '))';
        // Управление встаёт СБОКУ от высокого кадра — иначе справа от него
        // остаётся пустая треть панели.
        var panel = el.box.closest('.vr-stagepanel');
        if (panel) panel.classList.toggle('tall', portrait);
        // ⚠️ В боковом макете колонка кадра шириной `auto`, а холсты внутри
        // коробки позиционированы абсолютно — то есть собственной ширины у неё
        // НЕТ, и `min(100%, …)` считать не от чего. Без явной ширины обёртки
        // колонка схлопывалась до минимумов, и дорожка плеера превращалась в
        // шестидесятипиксельный огрызок (замерено на вертикальном ролике).
        var media = el.box.closest('.vr-media');
        if (media) media.style.width = portrait ? 'calc(' + cap + ' * ' + ratio + ')' : '';
    }

    // ════════════════════════════════════════════════════════════════════════
    //  Кадр по времени — БЕЗ лишнего ожидания
    // ════════════════════════════════════════════════════════════════════════
    /**
     * `seeked` по спецификации означает: позиция установлена и кадр доступен
     * для отрисовки. Ждать после него `requestVideoFrameCallback` бессмысленно
     * — у видео на паузе показа не происходит, колбэк не приходит, и всё
     * упирается в страховочный таймер. Проверено побайтово (см. шапку файла).
     *
     * Срок оставлен: `seeked` приходит не всегда (битый или необычный
     * контейнер), а зависшая выдача кадра вешает энкодер навсегда. По
     * истечении срока берём ТЕКУЩИЙ кадр и считаем промах — систематический
     * промах обязан быть назван, а не замаскирован.
     */
    function seek(t, deadlineMs) {
        return new Promise(function (res) {
            var v = el.video, done = false, timer = null, t0 = performance.now();
            var target = Math.max(0, Math.min(t, st.dur - 0.02));
            var ok = function (missed) {
                if (done) return;
                done = true;
                if (timer) clearTimeout(timer);
                if (missed) st.seekMisses++;
                st.seekTotal++;
                // ВЫДАЧА кадра тоже стоит времени, и на слабой машине —
                // сравнимо с самой обработкой. Оценка, считавшая только
                // `proc.frame`, обещала 39 секунд там, где выходило 90.
                // Скользящее среднее, а не последнее значение: первая
                // перемотка после загрузки всегда дороже прочих.
                var dt = performance.now() - t0;
                st.msPerSeek = st.msPerSeek ? st.msPerSeek * 0.8 + dt * 0.2 : dt;
                res(v);
            };
            if (Math.abs(v.currentTime - target) < 1e-4 && v.readyState >= 2) return ok(false);
            v.onseeked = function () { ok(false); };
            v.onerror = function () { ok(true); };
            timer = setTimeout(function () { ok(true); }, deadlineMs || SEEK_DEADLINE_MS);
            try { v.currentTime = target; } catch (e) { ok(true); }
        });
    }

    // ════════════════════════════════════════════════════════════════════════
    //  Разбор сцены
    // ════════════════════════════════════════════════════════════════════════
    async function readScene() {
        veil(true, I.scene || '', 0.08, '');
        try {
            st.scene = await window.FRVideo.prepare(seek, {
                // Пять выборок и маленький кадр: сцене нужны только цвет света
                // и масштаб лица, и то и другое на 300 px читается так же, как
                // на 380. Разбор блокирует кнопку, и каждая лишняя секунда
                // здесь — секунда ожидания у человека.
                duration: st.dur, samples: 5, sources: CFG.sources || [],
                workMax: 300,
                onProgress: function (p) { veil(true, I.scene || '', 0.08 + p * 0.86, ''); },
            });
            st.ready = !!(st.scene && st.scene.ok);
            // Разбор сцены дешевле кадрового прохода, но САМИ КАДРЫ считаем на
            // прежнем разрешении: там от него зависит результат.
            if (st.scene) st.scene.workMax = 380;
            st.proc = st.ready ? window.FRVideo.createProcessor(st.scene, {}) : null;
            ae('feature_use', {
                f: 'scene_read',
                faceCov: st.scene ? +(st.scene.faceCoverage * 100).toFixed(0) : 0,
                // Что видел детектор на каждой выборке (лица, яркость кадра) и
                // сколько стоил поиск: «лицо не найдено» на чёрном кадре после
                // перемотки и на ролике без лица — разные диагнозы.
                samples: st.scene && st.scene.samples ? st.scene.samples.map(function (s) { return s.faces + ':' + s.luma; }).join(',') : '',
                tiles: st.scene && st.scene.finderStats ? st.scene.finderStats.tiles : 0,
                tileHits: st.scene && st.scene.finderStats ? st.scene.finderStats.tileHits : 0,
                lightOk: st.scene && st.scene.light ? (st.scene.light.confident ? 1 : 0) : 0,
            });
            stageOnce('scene', {
                sec: Math.round(st.dur), w: st.vw, h: st.vh, fps: st.srcFps,
                ms: Math.round(performance.now() - st.t0),
                cov: st.scene ? Math.round(st.scene.faceCoverage * 100) : 0,
            });
        } catch (err) {
            st.ready = false;
            var detectorDead = !!(window.FRLandmarks && window.FRLandmarks.isUnavailable && window.FRLandmarks.isUnavailable(err));
            if (detectorDead) {
                // Ни тоста, ни сырого «Aborted()»: причина — на экране, органы
                // погашены (см. showFail). Лестница кандидатов внутри
                // landmarks.js уже перебрала всё (CPU и GPU на каждом
                // источнике) — если мы здесь, распознавания в этом браузере
                // нет, и говорить надо это, а не «лицо не найдено».
                st.modelFailed = true;
                showFail('detector');
                setRailEnabled(false);
                if (el.badge) el.badge.hidden = true;
                ae('tool_error', { f: 'scene_failed', reason: 'detector', msg: String(err && err.message).slice(0, 120) });
            } else {
                window.__handleError && window.__handleError(err, SLUG);
                ae('tool_error', { f: 'scene_failed', msg: String(err && err.message).slice(0, 120) });
            }
        } finally {
            veil(false);
            showTruth();
            setProgress(false);
        }
    }

    // ════════════════════════════════════════════════════════════════════════
    //  Предпросмотр
    // ════════════════════════════════════════════════════════════════════════
    function params() {
        var g = function (k) { return el.sl[k] ? (+el.sl[k].value) / 100 : 0; };
        return {
            shine: g('shine'), smooth: g('smooth'), red: g('red'), eye: g('eye'),
            teeth: g('teeth'), sclera: g('sclera'),
            exposure: g('exposure'), warmth: g('warmth'),
            spots: st.spots,
            mainFaceOnly: !!(el.mainFace && el.mainFace.checked),
        };
    }

    /**
     * Видны ли зубы на ЭТОМ кадре. Отбеливание работает только там, где рот
     * открыт; на кадре с сомкнутыми губами ползунок честно ничего не меняет, и
     * без этой строки это неотличимо от поломки — человек сдвигает ползунок,
     * смотрит на стоп-кадр, где рот закрыт, и уходит с «не работает».
     */
    function showTeethState(r) {
        if (!el.teethState) return;
        var searched = r && r.teethPx !== undefined && r.teethPx >= 0;
        var hide = !searched || r.teethPx > 0 || !(params().teeth > 0);
        el.teethState.hidden = hide;
        if (!hide) el.teethState.textContent = I.teeth_not_visible || '';
    }

    var previewTimer = null;
    function schedulePreview(delay) {
        clearTimeout(previewTimer);
        previewTimer = setTimeout(renderPreview, delay === undefined ? 130 : delay);
    }

    function drawBefore() {
        try { el.before.getContext('2d').drawImage(el.video, 0, 0, st.outW, st.outH); } catch (_) { }
    }

    async function renderPreview() {
        // ⚠️ Во время экспорта к <video> прикасаться НЕЛЬЗЯ: экспорт идёт по
        // тому же элементу покадрово, и наша перемотка увела бы его кадр.
        if (st.exporting) return;
        if (!st.ready || st.busy) { st.pending = true; return; }
        if (st.motion && st.motion.playing) return;   // петля рисует сама
        st.busy = true;
        try {
            drawBefore();
            var actx = el.after.getContext('2d', { willReadFrequently: true });
            // Предпросмотр — независимый кадр, поэтому память между кадрами
            // сбрасываем: иначе стоп-кадр унаследует сглаживание от места,
            // где стояла перемотка до этого.
            st.proc.reset();
            // …но подсказать, где лицо было: окно слежения найдёт его за один
            // дешёвый проход вместо полного кадра и плиток.
            if (st.lastFaceBox && st.proc.finder) st.proc.finder.seed(st.lastFaceBox);
            var tf = performance.now();
            var r = await st.proc.frame(el.video, actx, st.outW, st.outH, params(), { explain: st.cmp === 'explain' });
            if (r && r.faceBoxNorm) st.lastFaceBox = r.faceBoxNorm;
            st.explainData = (r && r.explain) || null;
            st.lastMainFace = (r && r.mainFace) || null;
            drawExplain();
            showTeethState(r);
            // Стоимость кадра берём отсюда — предпросмотр её всё равно платит.
            // Экспорт крупнее ровно во столько раз, во сколько больше площадь
            // ОБЛАСТИ ЛИЦА, а тяжёлая часть (разбор на уменьшенной копии)
            // от размера выдачи не зависит вовсе — замер 04.09: 854×480 192 мс,
            // 1280×720 175 мс, 1920×1080 179 мс на одном ролике.
            st.msPerFrame = performance.now() - tf;
            updatePlan();
        } catch (err) {
            window.__handleError && window.__handleError(err, SLUG);
        } finally {
            st.busy = false;
            if (st.pending) { st.pending = false; renderPreview(); }
        }
    }

    // ════════════════════════════════════════════════════════════════════════
    //  Плеер
    // ════════════════════════════════════════════════════════════════════════
    function setPlayingUI(on) {
        st.playing = !!on;
        drawExplain();
        if (el.play) {
            el.play.setAttribute('aria-pressed', on ? 'true' : 'false');
            el.play.setAttribute('aria-label', (on ? I.pause_label : I.play_label) || '');
        }
        setBadge();
    }

    function bindPlayer() {
        if (!el.play) return;
        el.play.addEventListener('click', togglePlay);

        // Живое положение головки — от самого видео, а не от таймера: так оно
        // не разъезжается с картинкой при подтормаживании.
        var raf = null;
        var follow = function () {
            if (!st.playing) return;
            var t = el.video.currentTime;
            if (t >= st.outT - 0.02) { el.video.pause(); return; }
            textWrite(el.timeCur, fmt(t));
            drawHead(t);
            drawBefore();
            raf = requestAnimationFrame(follow);
        };
        el.video.addEventListener('play', function () { setPlayingUI(true); cancelAnimationFrame(raf); raf = requestAnimationFrame(follow); });
        el.video.addEventListener('pause', function () {
            setPlayingUI(false); cancelAnimationFrame(raf);
            textWrite(el.timeCur, fmt(el.video.currentTime));
            drawHead(el.video.currentTime);
            schedulePreview(0);
        });
        el.video.addEventListener('ended', function () { setPlayingUI(false); });

        // Дорожка: клик и перетаскивание головки, ручки фрагмента.
        var dragging = null;
        var posOf = function (ev) {
            var r = el.track.getBoundingClientRect();
            var pad = parseFloat(getComputedStyle(el.track).getPropertyValue('--vr-pad')) || 0;
            var x = (ev.touches ? ev.touches[0].clientX : ev.clientX) - r.left - pad;
            return Math.max(0, Math.min(1, x / Math.max(1, r.width - 2 * pad))) * st.dur;
        };
        var down = function (ev) {
            if (!st.dur) return;
            dragging = ev.target === el.inH ? 'in' : ev.target === el.outH ? 'out' : 'head';
            move(ev);
            ev.preventDefault();
        };
        var move = function (ev) {
            if (!dragging) return;
            var t = posOf(ev);
            if (dragging === 'in') setRange(Math.min(t, st.outT - 0.2), st.outT);
            else if (dragging === 'out') setRange(st.inT, Math.max(t, st.inT + 0.2));
            else goTo(t);
        };
        var up = function () { if (dragging) { dragging = null; if (!st.playing) schedulePreview(0); } };
        el.track.addEventListener('mousedown', down);
        el.track.addEventListener('touchstart', down, { passive: false });
        window.addEventListener('mousemove', move);
        window.addEventListener('touchmove', move, { passive: true });
        window.addEventListener('mouseup', up);
        window.addEventListener('touchend', up);

        var keyStep = function (handle) {
            return function (e) {
                var step = e.shiftKey ? 1 : (st.srcFps ? 1 / st.srcFps : 0.04);
                var d = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
                if (!d) return;
                e.preventDefault();
                if (handle === 'in') setRange(Math.min(Math.max(0, st.inT + d), st.outT - 0.2), st.outT);
                else setRange(st.inT, Math.max(Math.min(st.dur, st.outT + d), st.inT + 0.2));
                schedulePreview(140);
            };
        };
        el.inH.addEventListener('keydown', keyStep('in'));
        el.outH.addEventListener('keydown', keyStep('out'));

        // Пробел/стрелки на самом кадре — привычка любого плеера.
        el.box.addEventListener('keydown', function (e) {
            if (e.key === ' ') { e.preventDefault(); togglePlay(); }
            else if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(el.video.currentTime - (e.shiftKey ? 5 : 1)); schedulePreview(120); }
            else if (e.key === 'ArrowRight') { e.preventDefault(); goTo(el.video.currentTime + (e.shiftKey ? 5 : 1)); schedulePreview(120); }
        });
        el.box.setAttribute('tabindex', '0');
    }

    function togglePlay() {
        stopMotion();
        if (st.playing) { el.video.pause(); return; }
        // Звук включаем только по нажатию человека: до этого элемент обязан
        // быть немым, иначе автовоспроизведение при разборе сцены не стартует.
        el.video.muted = false;
        if (el.video.currentTime < st.inT - 0.05 || el.video.currentTime >= st.outT - 0.02) el.video.currentTime = st.inT;
        var p = el.video.play();
        if (p && p.catch) p.catch(function () { setPlayingUI(false); });
        stageOnce('played', {});
    }

    function goTo(t) {
        t = Math.max(0, Math.min(st.dur - 0.02, t));
        el.video.currentTime = t;
        textWrite(el.timeCur, fmt(t));
        drawHead(t);
    }

    function setRange(a, b) {
        st.inT = Math.max(0, Math.min(a, st.dur));
        st.outT = Math.max(st.inT + 0.2, Math.min(b, st.dur));
        drawRange();
        updatePlan();
        ae('tool_interact', { f: 'range', sec: Math.round(st.outT - st.inT) });
    }

    function drawRange() {
        if (!st.dur) return;
        var a = st.inT / st.dur, b = st.outT / st.dur;
        // Внутри поля --vr-pad: иначе крайние ручки наполовину висят снаружи
        // дорожки и по ним не попасть.
        var at = function (f) { return 'calc(var(--vr-pad) + (100% - 2 * var(--vr-pad)) * ' + f.toFixed(4) + ')'; };
        el.sel.style.insetInlineStart = at(a);
        el.sel.style.width = 'calc((100% - 2 * var(--vr-pad)) * ' + (b - a).toFixed(4) + ')';
        el.inH.style.insetInlineStart = at(a);
        el.outH.style.insetInlineStart = at(b);
        a *= 100; b *= 100;
        el.inH.setAttribute('aria-valuenow', Math.round(a));
        el.outH.setAttribute('aria-valuenow', Math.round(b));
        el.inH.setAttribute('aria-valuetext', fmt(st.inT));
        el.outH.setAttribute('aria-valuetext', fmt(st.outT));
        drawHead(el.video ? el.video.currentTime : 0);
    }

    function drawHead(t) {
        if (!st.dur || !el.head) return;
        var f = Math.max(0, Math.min(1, t / st.dur));
        el.head.style.insetInlineStart = 'calc(var(--vr-pad) + (100% - 2 * var(--vr-pad)) * ' + f.toFixed(4) + ')';
    }

    // ════════════════════════════════════════════════════════════════════════
    //  Режимы сравнения и лупа
    // ════════════════════════════════════════════════════════════════════════
    function bindCompare() {
        if (!el.cmp) return;
        el.cmp.addEventListener('click', function (e) {
            var b = e.target.closest('.tc-seg-b');
            if (!b) return;
            setCompare(b.dataset.cmp);
            ae('tool_interact', { f: 'compare', mode: b.dataset.cmp });
        });
        setCompare('wipe');
    }

    function setCompare(mode) {
        st.cmp = mode;
        var bs = el.cmp.querySelectorAll('.tc-seg-b');
        for (var i = 0; i < bs.length; i++) {
            var on = bs[i].dataset.cmp === mode;
            bs[i].classList.toggle('on', on);
            bs[i].setAttribute('aria-selected', on ? 'true' : 'false');
        }
        el.box.classList.toggle('wipe', mode === 'wipe');
        el.box.classList.toggle('explain', mode === 'explain');
        el.after.style.display = mode === 'before' ? 'none' : '';
        if (mode === 'wipe') setWipe(st.wipe);
        else el.after.style.clipPath = 'none';
        // Карта «что правится» считается вместе с кадром предпросмотра —
        // при входе в режим её ещё нет, значит кадр пересчитываем.
        if (mode === 'explain' && !st.explainData) schedulePreview(0);
        drawExplain();
    }

    /**
     * «Что правится» — вместо прежней «Разницы» (|после − до|, усиленная
     * ×10, белым по чёрному): её выбирали 107 сессий из 458 за 60 дней, но
     * она не говорила, ЧТО изменилось и почему. Здесь поверх приглушённого
     * чёрно-белого кадра цветом показано, где и насколько сильно действует
     * каждая группа правок, а защищённые черты (глаза, брови, губы) обведены:
     * видно, почему блик где-то не ушёл (например, это зона глаза или выбитый
     * в белое участок) и что сглаживание не трогает черты.
     * Яркость цвета — сила правки ОТНОСИТЕЛЬНО самой сильной в кадре для этой
     * группы (иначе слабое сглаживание не видно рядом со снятием блика).
     */
    var EXPLAIN = [
        { key: 'skin', rgb: [61, 220, 132], floor: 0.006, alpha: 0.5 },
        { key: 'shine', rgb: [255, 176, 32], floor: 0.02, alpha: 0.95 },
        { key: 'feat', rgb: [76, 201, 255], floor: 0.02, alpha: 0.95 },
        { key: 'spot', rgb: [255, 111, 176], floor: 0.02, alpha: 0.95 },
    ];
    function drawExplain() {
        if (!el.explain) return;
        var ex = st.explainData;
        var moving = st.playing || (st.motion && st.motion.playing);
        var on = st.cmp === 'explain' && !!ex && !moving;
        el.explain.hidden = !on;
        if (el.legend) el.legend.hidden = !(st.cmp === 'explain' && !moving);
        if (!on) return;
        var W = st.outW, H = st.outH;
        if (el.explain.width !== W || el.explain.height !== H) { el.explain.width = W; el.explain.height = H; }
        var cx = el.explain.getContext('2d', { willReadFrequently: true });
        cx.drawImage(el.before, 0, 0, W, H);
        var img = cx.getImageData(0, 0, W, H), d = img.data;
        // Основа — исходный кадр в сером и приглушённый: цвет правок на нём читается.
        for (var i = 0; i < d.length; i += 4) {
            var g = (0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2]) * 0.55 + 18;
            d[i] = d[i + 1] = d[i + 2] = g;
        }
        // Масштаб каждой группы — её 99-й процентиль в области (не ниже порога шума).
        var scale = EXPLAIN.map(function (g) {
            var a = ex[g.key], v = [];
            for (var q = 0; q < a.length; q += 7) if (a[q] > 0) v.push(a[q]);
            if (!v.length) return 0;
            v.sort(function (x, y) { return x - y; });
            return Math.max(g.floor, v[Math.floor(v.length * 0.99)]);
        });
        for (var y = 0; y < ex.h; y++) {
            var oy = ex.y + y;
            if (oy < 0 || oy >= H) continue;
            for (var x = 0; x < ex.w; x++) {
                var ox = ex.x + x;
                if (ox < 0 || ox >= W) continue;
                // Слоями, а не «кто сильнее»: сглаживание идёт по всей коже и
                // перекрыло бы блик, а вопрос чаще всего именно «где снят блик».
                var j = y * ex.w + x, o = (oy * W + ox) * 4;
                for (var gi = 0; gi < EXPLAIN.length; gi++) {
                    if (!scale[gi]) continue;
                    var hv = Math.min(1, ex[EXPLAIN[gi].key][j] / scale[gi]);
                    if (hv < 0.08) continue;
                    var al = Math.pow(hv, 0.6) * EXPLAIN[gi].alpha, c = EXPLAIN[gi].rgb;
                    d[o] = d[o] * (1 - al) + c[0] * al;
                    d[o + 1] = d[o + 1] * (1 - al) + c[1] * al;
                    d[o + 2] = d[o + 2] * (1 - al) + c[2] * al;
                }
            }
        }
        cx.putImageData(img, 0, 0);
        // Защищённые черты — пунктиром: по ним ретушь кожи не идёт.
        var lw = Math.max(2, W / 480);
        cx.save();
        cx.lineWidth = lw; cx.setLineDash([lw * 3, lw * 2]); cx.strokeStyle = 'rgba(155,188,255,0.95)';
        (ex.faces || []).forEach(function (fc) {
            [fc.leftEye, fc.rightEye, fc.lips, fc.leftBrow, fc.rightBrow].forEach(function (pg) {
                if (!pg || pg.length < 3) return;
                cx.beginPath();
                for (var k = 0; k < pg.length; k++) {
                    var px = ex.x + pg[k][0] * ex.w, py = ex.y + pg[k][1] * ex.h;
                    if (k) cx.lineTo(px, py); else cx.moveTo(px, py);
                }
                cx.closePath(); cx.stroke();
            });
        });
        cx.restore();
    }

    // ════════════════════════════════════════════════════════════════════════
    //  Пятна: отметил на одном кадре — убрано на всём ролике
    // ════════════════════════════════════════════════════════════════════════
    /**
     * Прыщ или родинку указывает человек (автоматический поиск дефектов здесь
     * не различает прыщ и пору — см. шапку heal.js), а дальше пятно живёт в
     * системе ГЛАВНОГО лица: 12 ближайших точек MediaPipe на отмеченном кадре,
     * на каждом другом — локальная аффинная подгонка по ним (FRHeal.trackSpot).
     * Донор — кожа, которой закрывается пятно, — выбирается один раз, здесь, по
     * исходному кадру, и дальше едет вместе с лицом: выбранный заново на каждом
     * кадре, он менял бы место, и текстура внутри пятна дрожала бы.
     */
    function bindSpots() {
        if (!el.spotOn) return;
        el.spotOn.addEventListener('click', function () {
            if (!st.ready) return;
            setAim(!st.aim);
        });
        el.spotUndo && el.spotUndo.addEventListener('click', function () {
            if (!st.spots.length) return;
            st.spots.pop(); syncSpots(); schedulePreview(0);
            ae('tool_interact', { f: 'spot_undo', n: st.spots.length });
        });
        el.spotClear && el.spotClear.addEventListener('click', function () {
            if (!st.spots.length) return;
            st.spots = []; syncSpots(); schedulePreview(0);
            ae('tool_interact', { f: 'spot_clear' });
        });
        if (el.spotSize) {
            window.__toolSettings && window.__toolSettings.bind(SLUG, el.spotSize, 'spot_size', el.spotSize.value);
            var out = function () { if (el.spotSizeOut) el.spotSizeOut.textContent = el.spotSize.value; };
            el.spotSize.addEventListener('input', out);
            out();
        }
        // Ушли с вкладки «Пятна» — прицел снимается: иначе клик по кадру на
        // другой вкладке ставил бы пятно, которого человек не просил.
        el.tabs && el.tabs.addEventListener('click', function (e) {
            var b = e.target.closest('[data-tab]');
            if (b && b.getAttribute('data-tab') !== 'spot') setAim(false);
            if (b && b.getAttribute('data-tab') === 'spot') syncSpots();
        });
        el.box.addEventListener('mousemove', moveAimRing);
        el.box.addEventListener('mouseleave', function () { if (el.aimRing) el.aimRing.hidden = true; });
        syncSpots();
    }

    function setAim(on) {
        st.aim = !!on;
        if (el.spotOn) {
            el.spotOn.setAttribute('aria-pressed', st.aim ? 'true' : 'false');
            el.spotOn.textContent = (st.aim ? I.spot_on : I.spot_start) || '';
        }
        if (el.box) el.box.classList.toggle('aim', st.aim);
        if (!st.aim && el.aimRing) el.aimRing.hidden = true;
        syncSpots();
    }

    function clearSpots() {
        st.spots = [];
        st.lastMainFace = null;
        if (st.aim) setAim(false); else syncSpots();
    }

    function syncSpots() {
        var n = st.spots.length;
        if (el.spotUndo) el.spotUndo.disabled = !n;
        if (el.spotClear) el.spotClear.disabled = !n;
        var tip = st.aim ? (I.spot_prompt || '') : (n ? (I.spot_count || '').replace(':n', n) : (I.spot_hint || ''));
        if (el.paneSpot) {
            el.paneSpot.setAttribute('data-hint', tip);
            if (!el.paneSpot.hidden && el.hint) el.hint.textContent = tip;
        }
    }

    /** Точка события → пиксели кадра предпросмотра (с учётом вписывания и лупы). */
    function frameXY(ev) {
        var r = el.box.getBoundingClientRect();
        var t = ev.touches && ev.touches[0] ? ev.touches[0] : ev;
        var cx = t.clientX - r.left, cy = t.clientY - r.top;
        var W = st.outW, H = st.outH;
        if (st.zoom) {
            // Лупа: object-fit:none — пиксель кадра = CSS-пиксель, сдвиг задаёт
            // object-position в процентах (--vr-zx / --vr-zy).
            var zx = parseFloat(el.box.style.getPropertyValue('--vr-zx'));
            var zy = parseFloat(el.box.style.getPropertyValue('--vr-zy'));
            zx = isFinite(zx) ? zx / 100 : 0.5; zy = isFinite(zy) ? zy / 100 : 0.5;
            return { x: cx - (r.width - W) * zx, y: cy - (r.height - H) * zy, scale: 1, bx: cx, by: cy };
        }
        var s = Math.min(r.width / W, r.height / H);
        return { x: (cx - (r.width - W * s) / 2) / s, y: (cy - (r.height - H * s) / 2) / s, scale: s, bx: cx, by: cy };
    }

    function spotRadius() {
        return Math.max(3, Math.round((+(el.spotSize && el.spotSize.value) || 18) / 1000 * Math.max(st.outW, st.outH)));
    }

    function moveAimRing(ev) {
        if (!st.aim || !el.aimRing || !st.outW) return;
        var p = frameXY(ev), d = 2 * spotRadius() * p.scale;
        el.aimRing.style.width = d + 'px'; el.aimRing.style.height = d + 'px';
        el.aimRing.style.left = p.bx + 'px'; el.aimRing.style.top = p.by + 'px';
        el.aimRing.hidden = false;
    }

    function addSpotAt(ev) {
        if (!st.ready || !st.outW || !window.FRHeal || !window.FREngine) return;
        var p = frameXY(ev), W = st.outW, H = st.outH;
        if (!(p.x >= 0 && p.y >= 0 && p.x < W && p.y < H)) return;
        var mf = st.lastMainFace, fb = mf && mf.box;
        var onFace = !!(mf && mf.pts && fb &&
            p.x >= (fb.x - fb.w * 0.1) * W && p.x <= (fb.x + fb.w * 1.1) * W &&
            p.y >= (fb.y - fb.h * 0.1) * H && p.y <= (fb.y + fb.h * 1.1) * H);
        if (!onFace) {
            window.__toast && window.__toast(I.spot_no_face || '', 'warning');
            ae('tool_diagnostic', { issue: 'spot_no_face', face: mf ? 1 : 0 });
            return;
        }
        var r = spotRadius(), x = Math.round(p.x), y = Math.round(p.y);
        // Донор — по ИСХОДНОМУ кадру (холст «до»), в окрестности пятна.
        var pad = Math.ceil(r * 6) + 2;
        var x0 = Math.max(0, x - pad), y0 = Math.max(0, y - pad);
        var x1 = Math.min(W, x + pad + 1), y1 = Math.min(H, y + pad + 1);
        var donor = null;
        try {
            var id = el.before.getContext('2d').getImageData(x0, y0, x1 - x0, y1 - y0);
            donor = window.FRHeal.findDonor(window.FREngine.toPlanes(id), x - x0, y - y0, r, {});
        } catch (_) { donor = null; }
        var spot = donor ? window.FRHeal.makeSpot(mf.pts, W, H, x, y, r, donor) : null;
        if (!spot) {
            window.__toast && window.__toast(I.spot_missed || '', 'warning');
            ae('tool_diagnostic', { issue: 'spot_no_donor', r: r });
            return;
        }
        st.spots.push(spot);
        syncSpots();
        ae('feature_use', { f: 'spot_add', n: st.spots.length, r: Math.round(1000 * r / Math.max(W, H)) });
        schedulePreview(0);
    }

    function setWipe(v) {
        st.wipe = Math.max(0, Math.min(1, v));
        var pct = (st.wipe * 100).toFixed(2) + '%';
        if (st.cmp === 'wipe') el.after.style.clipPath = 'inset(0 0 0 ' + pct + ')';
        el.wipe.style.left = pct;
        el.wipe.setAttribute('aria-valuenow', Math.round(st.wipe * 100));
    }

    function bindWipe() {
        if (!el.box) return;
        var drag = false;
        var pos = function (ev) {
            var r = el.box.getBoundingClientRect();
            var x = (ev.touches ? ev.touches[0].clientX : ev.clientX) - r.left;
            setWipe(x / Math.max(1, r.width));
        };
        el.box.addEventListener('mousedown', function (e) {
            if (st.aim) { addSpotAt(e); e.preventDefault(); return; }
            if (st.zoom) { panStart(e); return; }
            if (st.cmp !== 'wipe') return;
            drag = true; pos(e); e.preventDefault();
        });
        el.box.addEventListener('touchstart', function (e) {
            if (st.aim) { addSpotAt(e); e.preventDefault(); return; }
            if (st.zoom) { panStart(e); return; }
            if (st.cmp !== 'wipe') return;
            drag = true; pos(e); e.preventDefault();
        }, { passive: false });
        window.addEventListener('mousemove', function (e) { if (drag) pos(e); });
        window.addEventListener('touchmove', function (e) { if (drag) pos(e); }, { passive: true });
        window.addEventListener('mouseup', function () { drag = false; });
        window.addEventListener('touchend', function () { drag = false; });
        el.wipe.addEventListener('keydown', function (e) {
            var s = e.shiftKey ? 0.1 : 0.02;
            if (e.key === 'ArrowLeft') { setWipe(st.wipe - s); e.preventDefault(); }
            else if (e.key === 'ArrowRight') { setWipe(st.wipe + s); e.preventDefault(); }
        });
    }

    /* Лупа. Ретушь судят по порам, а вписанный в 500 px кадр их не показывает
       вовсе — на этом сгорает любой разговор «резина или не резина». */
    function toggleZoom() {
        st.zoom = !st.zoom;
        el.box.classList.toggle('zoom', st.zoom);
        el.zoomBtn.setAttribute('aria-pressed', st.zoom ? 'true' : 'false');
        el.zoomBtn.textContent = (st.zoom ? I.zoom_out : I.zoom_in) || '';
        if (st.zoom) {
            // Наводимся на лицо, а не в центр кадра: искать его руками — лишний шаг.
            var fb = st.lastFaceBox;
            var cx = fb ? fb.x + fb.w / 2 : 0.5, cy = fb ? fb.y + fb.h / 2 : 0.42;
            el.box.style.setProperty('--vr-zx', (cx * 100).toFixed(1) + '%');
            el.box.style.setProperty('--vr-zy', (cy * 100).toFixed(1) + '%');
        }
        ae('tool_interact', { f: 'zoom', on: st.zoom ? 1 : 0 });
    }

    function panStart(ev) {
        var r = el.box.getBoundingClientRect();
        el.box.classList.add('drag');
        var moveTo = function (e) {
            var x = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
            var y = (e.touches ? e.touches[0].clientY : e.clientY) - r.top;
            el.box.style.setProperty('--vr-zx', (Math.max(0, Math.min(1, x / r.width)) * 100).toFixed(1) + '%');
            el.box.style.setProperty('--vr-zy', (Math.max(0, Math.min(1, y / r.height)) * 100).toFixed(1) + '%');
        };
        moveTo(ev);
        var stop = function () {
            el.box.classList.remove('drag');
            window.removeEventListener('mousemove', moveTo);
            window.removeEventListener('touchmove', moveTo);
            window.removeEventListener('mouseup', stop);
            window.removeEventListener('touchend', stop);
        };
        window.addEventListener('mousemove', moveTo);
        window.addEventListener('touchmove', moveTo, { passive: true });
        window.addEventListener('mouseup', stop);
        window.addEventListener('touchend', stop);
        ev.preventDefault();
    }

    // ════════════════════════════════════════════════════════════════════════
    //  Проверка в движении (RAM preview)
    // ════════════════════════════════════════════════════════════════════════
    /**
     * Главное обещание тула — «эффект не дрожит между кадрами». На стоп-кадре
     * его проверить НЕЛЬЗЯ в принципе. Поэтому короткий отрезок собирается в
     * память и крутится по кругу — ровно как RAM preview в монтажной программе.
     *
     * Кадры держим парами (до/после), чтобы шторка работала и в движении.
     * Память ограничена явно: 2 × ширина × высота × 4 байта на кадр, и при
     * 1080p это 16 МБ на кадр пары — поэтому петля собирается в УМЕНЬШЕННОМ
     * размере, а не в размере выдачи.
     */
    async function onMotion() {
        if (st.motion && st.motion.playing) { stopMotion(); return; }
        // Во время СБОРКИ кнопка подписана «Остановить показ» — значит она
        // обязана останавливать. Без этой ветки нажатие уходило в `return`
        // ниже и не делало ничего: немой отказ кнопки.
        if (st.building) { st.abort && st.abort.abort(); return; }
        if (!st.ready || st.busy) return;
        stopEverything();

        var fps = Math.min(MOTION_FPS, st.srcFps || MOTION_FPS);
        var from = Math.max(st.inT, Math.min(el.video.currentTime, Math.max(st.inT, st.outT - 0.4)));
        var span = Math.min(MOTION_SEC, st.outT - from);
        var n = Math.max(2, Math.round(span * fps));

        // Петля — уменьшенная копия: 480 по высоте хватает, чтобы увидеть
        // дрожание, и стоит вчетверо меньше памяти, чем кадр выдачи.
        var mh = Math.min(480, st.outH), mw = Math.max(2, Math.round(st.outW * mh / st.outH));

        st.abort = new AbortController();
        var sig = st.abort.signal;
        st.building = true;
        veil(true, I.motion_build || '', 0, '', true);
        el.motionBtn.textContent = I.motion_stop || '';
        st.busy = true;

        var frames = [], t0 = workNow();
        try {
            var proc = window.FRVideo.createProcessor(st.scene, {});
            var pr = params();
            var bC = document.createElement('canvas'); bC.width = mw; bC.height = mh;
            var aC = document.createElement('canvas'); aC.width = mw; aC.height = mh;
            var bX = bC.getContext('2d'), aX = aC.getContext('2d', { willReadFrequently: true });
            for (var i = 0; i < n; i++) {
                if (sig.aborted) throw new DOMException('aborted', 'AbortError');
                await seek(from + i / fps);
                bX.drawImage(el.video, 0, 0, mw, mh);
                await proc.frame(el.video, aX, mw, mh, pr);
                frames.push({
                    before: await createImageBitmap(bC),
                    after: await createImageBitmap(aC),
                });
                var done = (i + 1) / n;
                var el2 = (workNow() - t0) / 1000;
                veil(true, I.motion_build || '', done,
                    (I.eta || '').replace(':t', human(Math.max(0, el2 / Math.max(0.02, done) - el2))), true);
            }
            st.motion = { frames: frames, fps: fps, from: from, idx: 0, playing: false, raf: null };
            veil(false);
            playMotion();
            ae('feature_use', {
                f: 'motion_preview', frames: n, fps: fps,
                buildMs: Math.round(workNow() - t0),
            });
        } catch (err) {
            for (var q = 0; q < frames.length; q++) { closePair(frames[q]); }
            veil(false);
            el.motionBtn.textContent = I.motion_check || '';
            if (!(err && err.name === 'AbortError')) {
                window.__handleError && window.__handleError(err, SLUG);
                ae('tool_error', { f: 'motion_failed', msg: String(err && err.message).slice(0, 120) });
            }
        } finally {
            st.busy = false;
            st.building = false;
            st.abort = null;
            setProgress(false);
        }
    }

    function closePair(p) {
        try { p.before && p.before.close && p.before.close(); } catch (_) { }
        try { p.after && p.after.close && p.after.close(); } catch (_) { }
    }

    function playMotion() {
        var m = st.motion;
        if (!m || !m.frames.length) return;
        m.playing = true;
        el.motionBtn.textContent = I.motion_stop || '';
        setBadge();
        drawExplain();
        var bX = el.before.getContext('2d'), aX = el.after.getContext('2d');
        var last = 0, step = 1000 / m.fps;
        var tick = function (now) {
            if (!m.playing) return;
            if (now - last >= step) {
                last = now;
                var f = m.frames[m.idx];
                bX.drawImage(f.before, 0, 0, st.outW, st.outH);
                aX.drawImage(f.after, 0, 0, st.outW, st.outH);
                textWrite(el.timeCur, fmt(m.from + m.idx / m.fps));
                drawHead(m.from + m.idx / m.fps);
                m.idx = (m.idx + 1) % m.frames.length;
            }
            m.raf = requestAnimationFrame(tick);
        };
        m.raf = requestAnimationFrame(tick);
    }

    function stopMotion() {
        var m = st.motion;
        if (!m) return;
        m.playing = false;
        if (m.raf) cancelAnimationFrame(m.raf);
        for (var i = 0; i < m.frames.length; i++) closePair(m.frames[i]);
        st.motion = null;
        if (el.motionBtn) el.motionBtn.textContent = I.motion_check || '';
        setBadge();
        schedulePreview(0);
    }

    // ════════════════════════════════════════════════════════════════════════
    //  Управление
    // ════════════════════════════════════════════════════════════════════════
    function bindSliders() {
        Object.keys(el.sl).forEach(function (k) {
            var input = el.sl[k];
            if (!input) return;
            window.__toolSettings && window.__toolSettings.bind(SLUG, input, 'sl_' + k, input.value);
            input.addEventListener('input', function () {
                paintOut(k);
                clearPresets();
                stageOnce('slider_' + k, { v: +input.value });
                schedulePreview(140);
            });
            paintOut(k);
        });
    }

    function paintOut(k) {
        if (!el.out[k] || !el.sl[k]) return;
        var v = +el.sl[k].value;
        // У знаковых ползунков («−40», «+25») процент не значит ничего: это
        // не «сколько сняли», а «в какую сторону сдвинули».
        el.out[k].textContent = (k === 'exposure' || k === 'warmth')
            ? (v > 0 ? '+' + v : String(v))
            : v + '%';
    }

    var PRESETS = {
        light:   { shine: 55,  smooth: 20, red: 15, eye: 10, teeth: 0,  sclera: 0 },
        // Зубы 45/80, а не 30/65 (25.09.2026): прежние числа подбирались под
        // отбеливание, которое почти не поднимало светлоту, — «естественно»
        // не меняло зубы видимо вовсе. Новое на 45 % даёт ΔL* ≈ +3 на зубах.
        natural: { shine: 100, smooth: 40, red: 30, eye: 25, teeth: 45, sclera: 20 },
        // Блеск 150 % (27.09.2026): выше 100 % досниваются вершины бликов —
        // на 100 % в бликовых зонах оставалась половина избытка над кожей.
        strong:  { shine: 150, smooth: 80, red: 65, eye: 50, teeth: 80, sclera: 50 },
    };

    /**
     * Подсвечен тот пресет, чьи значения РЕАЛЬНО стоят, иначе ни один. В
     * разметке «Естественно» горит всегда, а ползунки восстанавливаются из
     * сохранённых — и выходило, что выбран «Естественно» при зубах на нуле:
     * подсветка обещала отбеливание, которого не было.
     */
    function syncPresetHighlight() {
        var hit = null;
        Object.keys(PRESETS).some(function (name) {
            var p = PRESETS[name];
            var same = Object.keys(p).every(function (k) { return !el.sl[k] || +el.sl[k].value === p[k]; });
            if (same) hit = name;
            return same;
        });
        var b = document.querySelectorAll('#vrPresets .tc-seg-b');
        for (var i = 0; i < b.length; i++) {
            var on = b[i].dataset.preset === hit;
            b[i].classList.toggle('on', on);
            b[i].setAttribute('aria-selected', on ? 'true' : 'false');
        }
    }

    function clearPresets() {
        var b = document.querySelectorAll('#vrPresets .tc-seg-b');
        for (var i = 0; i < b.length; i++) { b[i].classList.remove('on'); b[i].setAttribute('aria-selected', 'false'); }
    }

    function applyPreset(name) {
        var p = PRESETS[name];
        if (!p) return;
        clearPresets();
        var btn = document.querySelector('#vrPresets [data-preset="' + name + '"]');
        if (btn) { btn.classList.add('on'); btn.setAttribute('aria-selected', 'true'); }
        Object.keys(p).forEach(function (k) {
            if (!el.sl[k]) return;
            el.sl[k].value = p[k];
            paintOut(k);
            window.__toolSettings && window.__toolSettings.save(SLUG, 'sl_' + k, String(p[k]));
        });
        // Свет и тон пресетами не трогаем: они про КОНКРЕТНУЮ съёмку, а не про
        // силу ретуши, и сбрасывать их вместе с «естественно» было бы сюрпризом.
        ae('tool_interact', { f: 'preset', name: name });
        schedulePreview(0);
    }

    function bindPresets() {
        var host = $('vrPresets');
        if (!host) return;
        host.addEventListener('click', function (e) {
            var btn = e.target.closest('.tc-seg-b');
            if (btn) applyPreset(btn.dataset.preset);
        });
    }

    // ════════════════════════════════════════════════════════════════════════
    //  Что человек видит и читает
    // ════════════════════════════════════════════════════════════════════════
    function veil(on, text, frac, sub, cancellable) {
        if (!el.veil) return;
        // Флаг ставим ДО setProgress: «поднята ли вуаль» — состояние тула, а не
        // свойство разметки, и читать его из DOM внутри setProgress значило
        // зависеть от порядка строк.
        st.veiled = !!on;
        el.veil.hidden = !on;
        if (!on) { setProgress(false); return; }
        el.veilTxt.textContent = text || '';
        el.veilFill.style.width = Math.round(Math.max(0, Math.min(1, frac || 0)) * 100) + '%';
        el.veilSub.textContent = sub || '';
        el.veilStop.hidden = !cancellable;
        setProgress(false);
    }

    function setBadge() {
        if (!el.badge) return;
        if (st.playing) { el.badge.hidden = false; el.badge.className = 'vr-badge'; el.badge.textContent = I.src_label || ''; return; }
        if (st.motion && st.motion.playing) { el.badge.hidden = false; el.badge.className = 'vr-badge'; el.badge.textContent = I.motion_playing || ''; return; }
        if (!st.scene) { el.badge.hidden = true; return; }
        var cov = st.scene.faceCoverage || 0;
        el.badge.hidden = false;
        if (!st.scene.ok) { el.badge.className = 'vr-badge warn'; el.badge.textContent = I.badge_none || ''; return; }
        if (cov < 0.99) {
            el.badge.className = 'vr-badge warn';
            el.badge.textContent = (I.badge_partial || '').replace(':pct', String(Math.round(cov * 100)));
        } else {
            el.badge.className = 'vr-badge';
            el.badge.textContent = I.badge_ok || '';
        }
    }

    /** Длинное объяснение — только когда есть что объяснять. */
    function showTruth() {
        setBadge();
        if (!el.truth) return;
        if (st.canExport === false) {
            el.truth.className = 'vr-truth warn';
            el.truth.textContent = I.no_export || '';
            return;
        }
        if (!st.scene) { el.truth.textContent = ''; return; }   // отказ детектора объясняет блок showFail('detector')
        if (!st.scene.ok) {
            el.truth.className = 'vr-truth warn';
            el.truth.textContent = (I.no_face || '') + ' ' + (I.no_face_hint || '');
            return;
        }
        var cov = st.scene.faceCoverage || 0;
        if (cov < 0.99) {
            el.truth.className = 'vr-truth warn';
            el.truth.textContent = (I.truth_partial || '').replace(':pct', String(Math.round(cov * 100)));
        } else {
            // ⚠️ В ХОРОШЕМ случае строки НЕТ. Значок на кадре уже говорит
            // «Лицо найдено», а обещание про текстуру и глаза живёт в рейке,
            // рядом с ползунками, которых оно касается. Раньше один и тот же
            // факт стоял в двух местах экрана разом и занимал ещё одну строку
            // под кадром — то есть платил высотой за повтор.
            el.truth.className = 'vr-truth';
            el.truth.textContent = '';
        }
    }

    /**
     * Строка под настройками экспорта: ЧТО именно уедет в файл и во что это
     * обойдётся по времени. Она обязана появиться ДО нажатия — полоса с
     * оставшимся временем честна, но показывается, когда человек уже согласился
     * ждать, а половина трафика видео-тулов это телефоны, где покадровая
     * обработка кратно дороже.
     */
    /**
     * СКОЛЬКО СТОИТ КАДР ВЫДАЧИ — И ПОЧЕМУ ЭТО НЕЛЬЗЯ ВЫВЕСТИ ИЗ ПРЕДПРОСМОТРА.
     *
     * ⚠️⚠️ Здесь стояло `st.msPerFrame + st.msPerSeek` — цена кадра
     * ПРЕДПРОСМОТРА, выданная за цену кадра выдачи. Замер 06.09.2026,
     * один ролик, 100 кадров:
     *
     *   480p — обещано 45 с, вышло  87 с  (в 1.9 раза)
     *   720p — обещано 46 с, вышло 192 с  (в 4.2 раза)
     *
     * Обещание не просто занижено — оно ОДИНАКОВО для 480p и 720p, хотя работа
     * отличается вдвое с лишним. Так и должно быть: тяжёлый разбор идёт на
     * уменьшенной копии постоянного размера, поэтому кадр предпросмотра стоит
     * примерно одинаково при любом выборе (прежний замер 04.09 — 192/175/179 мс
     * на 480/720/1080 — верен, просто мерил не то). А выдача вдобавок читает и
     * пишет полный кадр (профиль: 13.2 с readPixels на сотню кадров), держит
     * временное сглаживание между кадрами и кормит кодировщик — и растёт с
     * площадью.
     *
     * ⛔ Пересчётный коэффициент между путями пробовался и ОТВЕРГНУТ. На
     * свободной машине он ложился идеально (864 мс против 870 фактических на
     * 480p, 1938 против 1923 на 720p), но сам предпросмотр — живой замер: под
     * нагрузкой он вырос впятеро, и то же обещание стало «19 минут» там, где
     * работы на три. Ошибка просто сменила знак. Число, которое зависит от
     * того, что в этот момент делает соседняя вкладка, обещанием быть не может.
     *
     * Поэтому обещаем ТОЛЬКО измеренное на этой машине этим тулом. Пока такого
     * замера нет, честный ответ — «считаю время»: он появляется в первые
     * секунды после нажатия, когда полоса хода уже знает настоящую скорость.
     * Один раз подождавший человек второй раз видит правду, а не догадку.
     */
    function estPerFrame(w, h) {
        var measured = 0;
        try {
            measured = parseFloat(window.__toolSettings
                && window.__toolSettings.load(SLUG, 'msPerMpx', '0')) || 0;
        } catch (_) { }
        if (!measured) return 0;                       // «считаю время»
        var mpx = Math.max(0.01, (w * h) / 1e6);
        return measured * mpx + st.msPerSeek;
    }

    /**
     * Часы РАБОТЫ, а не стенные: вкладка, которую браузер заморозил в фоне,
     * или машина, уснувшая посреди экспорта, не работали — `performance.now()`
     * это время всё равно считает, и оценка «осталось» раздувалась до конца
     * прогона, а замер скорости ниже запоминал медленную машину, которой не было
     * (класс «≈30 ч на 89 %» в /video-fps, 26.09.2026). Один источник для
     * каждой пары «начало — сейчас» экспорта и превью движения.
     */
    function workNow() {
        return (typeof window.__activeNow === 'function') ? window.__activeNow() : performance.now();
    }

    /**
     * Запомнить настоящую скорость этой машины — в миллисекундах на мегапиксель
     * кадра. Именно на мегапиксель, а не на кадр: смена разрешения меняет цену
     * ровно пропорционально площади (проверено на тех же двух точках — 2.25
     * ожидаемых против 2.21 фактических), поэтому один замер годится для всех
     * пунктов списка.
     */
    function rememberRate(msTotal, frames, w, h) {
        if (!frames || !msTotal) return;
        var mpx = Math.max(0.01, (w * h) / 1e6);
        var perMpx = Math.round(msTotal / frames / mpx);
        if (!isFinite(perMpx) || perMpx <= 0) return;
        try { window.__toolSettings && window.__toolSettings.save(SLUG, 'msPerMpx', String(perMpx)); } catch (_) { }
    }

    function updatePlan() {
        if (!el.plan || !st.dur) return;
        var e = exportSize();
        st.expW = e.w; st.expH = e.h;
        var fps = st.srcFps || 30;
        var span = Math.max(0, st.outT - st.inT);
        var frames = Math.max(1, Math.round(span * fps));
        if (el.src) {
            el.src.textContent = (I.src_line || '')
                .replace(':w', String(st.vw)).replace(':h', String(st.vh))
                .replace(':fps', String(Math.round(fps)));
        }
        var head = (I.estimate || '')
            .replace(':from', fmt(st.inT)).replace(':to', fmt(st.outT))
            .replace(':frames', String(frames));
        // Про звук — ДО нажатия. Узнать, что ролик вышел немым, ПОСЛЕ трёх
        // минут ожидания — худший из возможных моментов.
        var aRate = audioPlan(st.dur);
        if (el.audio) {
            if (aRate === null) el.audio.textContent = I.audio_dropped || '';
            else if (aRate) el.audio.textContent = (I.audio_downsampled || '').replace(':khz', String(Math.round(aRate / 1000)));
            else el.audio.textContent = '';
            el.audio.hidden = !el.audio.textContent;
        }

        if (span > MAX_RANGE) {
            el.plan.textContent = (I.range_too_long || '').replace(':max', String(Math.round(MAX_RANGE / 60)));
            el.plan.style.color = 'var(--lg-warn)';
        } else if (estPerFrame(e.w, e.h) > 0) {
            var perFrame = estPerFrame(e.w, e.h);
            el.plan.textContent = head + ' · ' + (I.plan_time || '').replace(':t', human(frames * perFrame / 1000));
            el.plan.style.color = '';
            stageOnce('estimated', {
                sec: Math.round(frames * perFrame / 1000), frames: frames,
                msPerFrame: Math.round(st.msPerFrame), msPerSeek: Math.round(st.msPerSeek), h: e.h,
            });
        } else {
            el.plan.textContent = head + ' · ' + (I.estimate_wait || '');
            el.plan.style.color = '';
        }
        // Пересчёт плана не имеет права трогать полосу идущего экспорта:
        // setProgress(false) погасил бы её и открыл кнопку посреди работы.
        if (!st.exporting) setProgress(false);
    }

    /** Настройки на время экспорта закрыты: менять их всё равно нечему. */
    function lockControls(on) {
        var els = [];
        Object.keys(el.sl).forEach(function (k) { if (el.sl[k]) els.push(el.sl[k]); });
        [el.mainFace, el.height, el.format, el.reset].forEach(function (e) { if (e) els.push(e); });
        // Режимы сравнения и лупа НЕ закрываются: они чистое оформление, к
        // <video> не прикасаются, а смотреть, как кадр меняется по ходу
        // работы, — единственное развлечение на несколько минут ожидания.
        var seg = document.querySelectorAll('#vrPresets .tc-seg-b');
        for (var i = 0; i < seg.length; i++) els.push(seg[i]);
        for (var j = 0; j < els.length; j++) els[j].disabled = !!on;
    }

    function setProgress(on, stage, frac, eta) {
        if (!el.prog) return;
        el.prog.classList.toggle('on', !!on);
        if (stage !== undefined && el.stage) el.stage.textContent = stage || '';
        if (frac !== undefined && el.bar) el.bar.style.width = Math.round(Math.max(0, Math.min(1, frac)) * 100) + '%';
        if (el.eta) el.eta.textContent = eta || '';
        if (el.cancelBtn) el.cancelBtn.hidden = !on;
        if (el.exportBtn) {
            // Кнопка открыта, только когда нажатие может что-то дать. Без
            // `st.ready` она открывалась после НЕУДАЧНОГО разбора сцены, а
            // onExport выходил первой строкой — нажатие не давало ничего.
            // Пока на кадре вуаль (разбор ролика, сборка отрезка), экспорт
            // закрыт: он перезаписал бы st.abort, и цикл сборки продолжил бы
            // перематывать ТО ЖЕ <video>, по которому идёт выдача.
            var blocked = !!on || st.veiled || st.canExport === false || !st.ready
                || (st.outT - st.inT) > MAX_RANGE;
            el.exportBtn.disabled = blocked;
            // ⚠️ aria-disabled у кнопки ДВА владельца — тул (готовность) и блок
            // лимита (стена). Чужую пометку не снимаем: иначе поднятая стена
            // перестаёт читаться скринридером ровно тогда, когда тул готов.
            if (blocked) el.exportBtn.setAttribute('aria-disabled', 'true');
            else if (!el.exportBtn.classList.contains('tl-btn-blocked')) el.exportBtn.setAttribute('aria-disabled', 'false');
        }
        if (el.play) el.play.disabled = !!on;
        if (el.motionBtn) el.motionBtn.disabled = !!on || !st.ready;
    }

    // ════════════════════════════════════════════════════════════════════════
    //  Экспорт
    // ════════════════════════════════════════════════════════════════════════
    async function onExport() {
        if (!st.ready) {
            // Досюда доходят только в обход погашенной кнопки, но молчать
            // нельзя: отказ без причины неотличим от поломки.
            var why = st.canExport === false ? (I.no_export || '') : (I.no_face || '');
            if (why && window.__toast) window.__toast(why, 'warning');
            ae('tool_diagnostic', { issue: 'export_before_ready', canExport: st.canExport === false ? 0 : 1 });
            return;
        }
        var span = st.outT - st.inT;
        if (span > MAX_RANGE) {
            window.__toast && window.__toast((I.range_too_long || '').replace(':max', String(Math.round(MAX_RANGE / 60))), 'warning');
            return;
        }
        // ТОТ ЖЕ результат уже посчитан — отдать его, а не считать заново.
        // Жалоба 23.09.2026: после выгрузки шестиминутного 1080p (1 ч 39 мин)
        // человек нажал «Сохранить видео» ещё раз, и тул честно пересчитал всё
        // — ещё 35 минут ради файла, который уже лежал в памяти страницы.
        // Ключ — всё, что влияет на пиксели и контейнер: файл, фрагмент,
        // размер, формат и каждый ползунок.
        var outKey = JSON.stringify([
            st.limitKey || (st.file ? [st.file.name, st.file.size, st.file.lastModified] : ''),
            st.inT, st.outT, exportSize(), (el.format && el.format.value) || 'mp4', params(),
        ]);
        if (st.lastOut && st.lastOut.key === outKey) {
            var again = window.__downloadFile ? window.__downloadFile(st.lastOut.blob, st.lastOut.name) : false;
            if (again !== false && window.__toast) window.__toast(I.saved || '', 'success');
            ae('tool_funnel', { f: 'export_reused', sizeKB: Math.round(st.lastOut.blob.size / 1024), ok: again === false ? 0 : 1 });
            return;
        }
        // Настоящий новый прогон может переписать хранилище, на которое
        // ссылается прежний результат, — прежний больше не выдаём.
        st.lastOut = null;
        stopEverything();

        var run = null;
        if (window.__toolLimit && window.__toolLimit.begin) {
            run = window.__toolLimit.begin(SLUG, st.limitKey);
            if (!run) return;
        }

        var e = exportSize();
        var ew = e.w, eh = e.h;
        var fps = Math.max(5, Math.min(60, Math.round((st.srcFps || 30) * 1000) / 1000));
        var fmtKey = (el.format && el.format.value) || 'mp4';
        if (fmtKey === 'same') fmtKey = 'mp4';
        var container = fmtKey === 'webm' ? 'webm' : 'mp4';
        var pr = params();

        st.abort = new AbortController();
        st.exporting = true;
        lockControls(true);
        var lane = pickLane(container);
        stageOnce('export_start', {
            estSec: st.msPerFrame ? Math.round(span * fps * (st.msPerFrame + st.msPerSeek) / 1000) : 0,
            h: eh, fps: Math.round(fps), fmt: container, rangeSec: Math.round(span), lane: lane,
        });
        setProgress(true, I.exporting || '', 0);
        var t0 = workNow();
        var prog = { done: 0, total: Math.max(1, Math.round(span * fps)), teethFrames: 0, spotFrames: 0 };
        st.seekMisses = 0; st.seekTotal = 0;
        var track = null;

        try {
            var blob = null;
            if (lane === 'hw') {
                try {
                    var hw = await exportHw(ew, eh, fps, pr, prog, t0);
                    blob = hw.blob; track = hw.track;
                } catch (hwErr) {
                    if (isAbort(hwErr)) throw hwErr;
                    // Конвейер отказал (кодек, контейнер, память, смерть
                    // детектора посреди прохода) — тот же движок ретуши идёт
                    // покадровым проходом. Файл человек получит, только
                    // медленнее; причина уходит в телеметрию, а не в тишину.
                    ae('tool_diagnostic', { issue: 'hw_lane_failed', stage: (hwErr && hwErr.vrStage) || '', msg: String(hwErr && hwErr.message).slice(0, 140) });
                    lane = 'seek'; prog.done = 0;
                    setProgress(true, I.exporting || '', 0);
                }
            }
            if (!blob) blob = await exportSeek(ew, eh, fps, container, pr, span, prog, t0);
            if (!blob) throw new Error('empty-output');
            // Скорость ЭТОЙ машины теперь известна по настоящей выдаче —
            // запоминаем до раздачи файла, чтобы следующая оценка до нажатия
            // строилась на замере, а не на приближении.
            rememberRate(workNow() - t0, prog.done, ew, eh);

            var name = outputName(container);
            var ok = window.__downloadFile ? window.__downloadFile(blob, name) : false;
            st.lastOut = { key: outKey, blob: blob, name: name };
            if (ok !== false) {
                run && run.commit && run.commit();
                window.__toast && window.__toast(I.saved || '', 'success');
                var done = {
                    slug: SLUG, w: ew, h: eh, fps: Math.round(fps), fmt: container, lane: lane,
                    sec: Math.round(span), sizeKB: Math.round(blob.size / 1024),
                    msPerFrame: prog.done ? Math.round((workNow() - t0) / prog.done) : 0,
                    // Сила «Блеска» на выдаче: сколько людей уходит выше 100 %.
                    shine: Math.round((pr.shine || 0) * 100),
                };
                // В скольких кадрах выдачи зубы НАШЛИСЬ и отбелены — доля в %.
                // Жалоба 23.09.2026 («teeth are not any whiter») пришла от
                // человека, у которого отбеливание не сработало ни на одном
                // кадре шестиминутного ролика, а телеметрия этого не видела:
                // «ползунок сдвинут» и «зубы изменены» — разные события.
                if (pr.teeth > 0) done.teeth = prog.done ? Math.round(100 * prog.teethFrames / prog.done) : 0;
                // Пятна: сколько отмечено и в какой доле кадров убраны ВСЕ.
                if (st.spots.length) {
                    done.spots = st.spots.length;
                    done.spotFrames = prog.done ? Math.round(100 * prog.spotFrames / prog.done) : 0;
                }
                if (lane === 'seek') {
                    // Доля кадров, взятых по истечении срока: если она велика,
                    // результат мог получиться рваным, и это должно быть видно
                    // в телеметрии, а не только на глаз.
                    done.seekMissPct = st.seekTotal ? Math.round(100 * st.seekMisses / st.seekTotal) : 0;
                } else if (track) {
                    // Покрытие по ВСЕМ кадрам (а не по пяти выборкам), мосты через
                    // провалы и цена поиска — по ним видно, где детектор не тянет.
                    var fs = track.finderStats || {};
                    done.cov = Math.round(track.cov * 100); done.covRaw = Math.round(track.covRaw * 100);
                    done.bridged = track.bridged; done.frames = track.n;
                    done.probeTrack = fs.track; done.probeFull = fs.full; done.probeTiles = fs.tiles; done.tileHits = fs.tileHits;
                    // Окно слежения: сколько раз нашло лицо (trackHits/probeTrack — доля),
                    // и сколько лиц-дорожек вышло на ролик.
                    done.trackHits = fs.trackHits; done.faceTracks = track.tracks;
                    done.timeMiss = track.timeMiss || 0;
                    done.pool = prog.pool || 0;
                    if (prog.poolWhy) done.poolWhy = prog.poolWhy;
                    // Раскладка по проходам: поиск лица, ожидание воркеров, рисование.
                    done.trackS = Math.round((prog.trackMs || 0) / 100) / 10;
                    done.poolWaitS = Math.round((prog.poolWaitMs || 0) / 100) / 10;
                    done.renderS = Math.round((prog.renderMs || 0) / 100) / 10;
                }
                window.__aeToolComplete && window.__aeToolComplete(done);
                if (lane === 'seek' && st.seekTotal && st.seekMisses / st.seekTotal > 0.25) {
                    window.__toast && window.__toast(I.seek_missed || '', 'warning');
                    ae('tool_diagnostic', { issue: 'seek_misses', pct: Math.round(100 * st.seekMisses / st.seekTotal) });
                }
            } else {
                run && run.abort && run.abort();
            }
        } catch (err) {
            run && run.abort && run.abort();
            var aborted = isAbort(err);
            // Отмена — не ошибка, но её ДОЛЯ и МОМЕНТ говорят, не слишком ли
            // дорого обходится экспорт на живых устройствах.
            if (aborted) ae('tool_funnel', { f: 'export_cancelled', afterSec: Math.round((workNow() - t0) / 1000), frames: prog.done, lane: lane });
            window.__toast && window.__toast(aborted ? (I.cancelled || '') : (I.err_export || ''), aborted ? 'info' : 'error');
            if (!aborted) {
                window.__handleError && window.__handleError(err, SLUG);
                ae('tool_error', { f: 'export_failed', lane: lane, msg: String(err && err.message).slice(0, 140) });
            }
        } finally {
            st.abort = null;
            st.exporting = false;
            lockControls(false);
            setProgress(false);
            schedulePreview(0);
        }
    }

    /* The user's Cancel is the EXPORT's own signal, not a message containing
       «abort»: MediaPipe and the retouch worker die with «Aborted()», and read
       as a cancel a failed export said «cancelled» and skipped both of its
       fallbacks (hw → seek, pool → main thread). st.abort is still set at every
       caller — it is cleared in the export's finally. */
    function isAbort(err) {
        var sig = st.abort && st.abort.signal;
        if (window.__isUserCancel) return window.__isUserCancel(err, sig);
        return !!(sig ? sig.aborted : (err && err.name === 'AbortError'));
    }

    /**
     * КАКОЙ ДОРОГОЙ СОБИРАТЬ ВЫДАЧУ (06.09.2026).
     *
     * «hw» — два прохода через общий конвейер WebCodecs (webcodecs-pipeline.js):
     * аппаратный декодер отдаёт кадры ПО ПОРЯДКУ, без перемотки, звук
     * переносится байт в байт, поворот телефонной съёмки печётся в пиксели.
     * Первый проход только смотрит (geometry2d.analyzeOnly) и собирает
     * геометрию лица на КАЖДОМ кадре; второй рисует по уже известной
     * геометрии — сглаженной с заглядыванием вперёд и с мостами через
     * короткие провалы детектора (FRVideo.createTracker).
     *
     * «seek» — покадровая перемотка <video> + CanvasVideoExport: запасной
     * путь для контейнеров, которых mp4box не читает (webm/mkv), для WebM на
     * выходе и на случай отказа конвейера. Замерено на реальной съёмке
     * 23.976 к/с: перемотка на k/fps отдавала кадр k−1 на КАЖДОМ кадре —
     * картинка отставала от звука на кадр; поэтому перемотка теперь идёт на
     * СЕРЕДИНУ кадра, (k+0.5)/fps.
     */
    function pickLane(container) {
        var force = window.__vrLane;                  // отладка и тесты: 'hw' | 'seek'
        if (force === 'seek' || force === 'hw') return force;
        var P = window.WebCodecsPipeline;
        if (!P || typeof P.run !== 'function') return 'seek';
        if (!st.wcCap || !st.wcCap.ok) return 'seek';   // контейнер не разобран или декодер не взял кодек
        if (container !== 'mp4') return 'seek';
        return 'hw';
    }

    function hwProgress(prog, t0, phase, p) {
        // Доли проходов — по замеру: первый (декод + поиск лица) ≈ 30 % времени.
        var ranges = { track: [0, 0.3], render: [0.3, 1] };
        var r = ranges[phase] || [0, 1];
        var f = r[0] + (r[1] - r[0]) * Math.max(0, Math.min(1, p || 0));
        var elapsed = (workNow() - t0) / 1000;
        var eta = f > 0.03 ? Math.max(0, elapsed / f - elapsed) : 0;
        // Во втором проходе — по его собственному ходу. Доля первого прохода
        // (30 %) — замер для кадра на главном потоке; с воркерами проход
        // рисования короче в разы, и общая доля врала бы до самого конца.
        if (phase === 'render' && prog.renderT0) {
            var el2 = (workNow() - prog.renderT0) / 1000, p2 = Math.max(0, Math.min(1, p || 0));
            eta = p2 > 0.03 ? Math.max(0, el2 / p2 - el2) : 0;
        }
        var label = phase === 'track'
            ? (I.tracking || I.exporting || '')
            : (I.frames_done || '').replace(':done', String(prog.done)).replace(':total', String(prog.total));
        setProgress(true, label, f, eta ? (I.eta || '').replace(':t', human(eta)) : '');
    }

    /** Два прохода конвейера: геометрия по всем кадрам → выдача. */
    async function exportHw(ew, eh, fps, pr, prog, t0) {
        // Воркеры поднимаются ПАРАЛЛЕЛЬНО первому проходу: загрузка движка
        // (семь скриптов на воркер) прячется за поиском лица по всем кадрам.
        // Не поднялись за отведённое — второй проход идёт на главном потоке.
        var nPool = poolSize(ew, eh);
        prog.pool = 0; prog.poolWhy = '';
        var poolP = nPool >= 2
            ? window.FRVideo.createPool(st.scene, {}, { workerUrl: CFG.retouchWorker, size: nPool, startTimeoutMs: 90000 })
                .catch(function (ep) { prog.poolWhy = String((ep && ep.message) || ep).slice(0, 80); return null; })
            : Promise.resolve(null);
        try {
            return await exportHwPasses(ew, eh, fps, pr, prog, t0, nPool, poolP);
        } finally {
            poolP.then(function (pl) { if (pl) pl.close(); });
        }
    }

    async function exportHwPasses(ew, eh, fps, pr, prog, t0, nPool, poolP) {
        var P = window.WebCodecsPipeline;
        var W = st.vw, H = st.vh;                     // размер ПОКАЗА (поворот учтён)
        var stage = 'track';
        var fail = function (err, s) { if (err && !err.vrStage) { try { err.vrStage = s; } catch (_) { } } return err; };
        var pvA = el.after.getContext('2d');

        // ── Проход 1: только смотрим ─────────────────────────────────────
        var tracker = window.FRVideo.createTracker({ mainFaceOnly: !!pr.mainFaceOnly });
        var innerErr = null;
        try {
            await P.run({
                slug: SLUG, file: st.file, params: {}, crf: 23,
                trimStart: st.inT, trimEnd: st.outT,
                geometry2d: {
                    // Кодер требует чётных сторон даже там, где ничего не кодирует.
                    width: W - (W & 1), height: H - (H & 1), analyzeOnly: true,
                    draw: function (gctx, drawVideo, cw, ch, idx, t) {
                        if (innerErr) return;
                        drawVideo(0, 0, W, H, 0, 0, cw, ch);
                        try { tracker.push(gctx.canvas, cw, ch, t); }
                        catch (e2) { innerErr = e2; }
                    },
                },
                onProgress: function (p) { hwProgress(prog, t0, 'track', p); },
                abortSignal: st.abort.signal,
            });
        } catch (err) { throw fail(err, stage); }
        if (innerErr) throw fail(innerErr, 'track-detect');
        if (!tracker.count()) throw fail(new Error('hw-no-frames'), stage);
        var track = tracker.finish();
        track.finderStats = tracker.finder.stats;
        track.timeMiss = 0;

        // ── Проход 2: рисуем по известной геометрии ──────────────────────
        // Кадры считают воркеры (FRVideo.createPool): тот же движок, та же
        // выдача байт в байт, но N кадров в работе вместо одного. Пул не
        // поднялся или отказал посреди прохода — проход идёт заново прежним
        // путём на главном потоке: файл дороже задержки.
        stage = 'render';
        prog.trackMs = Math.round(workNow() - t0);
        var tw = workNow();
        var res, pool = await poolP;
        prog.poolWaitMs = Math.round(workNow() - tw);
        if (pool) prog.pool = nPool;
        try {
            try {
                res = await renderPass(pool);
            } catch (err) {
                if (!pool || !err || !err.vrPool || isAbort(err)) throw err;
                prog.poolWhy = String(err.message || err).slice(0, 80);
                ae('tool_diagnostic', { issue: 'retouch_pool_failed', msg: prog.poolWhy, frames: prog.done });
                pool.close(); pool = null; prog.pool = 0;
                res = await renderPass(null);
            }
        } catch (err) { throw fail(err, stage); }

        /** Один проход рисования: с пулом — кадры в работе параллельно, без — по одному. */
        function renderPass(pl) {
            var idx2 = 0;
            prog.renderT0 = workNow();
            // Без пула — свой обработчик на проход: память сглаживания во
            // времени обязана начаться с первого кадра и при повторе.
            var proc = pl ? null : window.FRVideo.createProcessor(st.scene, {});
            track.timeMiss = 0; prog.done = 0; prog.teethFrames = 0; prog.spotFrames = 0; innerErr = null;
            var at = function (t) {
                // Кадры обоих проходов идут в одном порядке; сверка по
                // времени ловит расхождение, если декодер выдал их иначе.
                var i = idx2++;
                var tt = track.timeAt(i);
                if (tt < 0 || Math.abs(tt - t) > 0.75 / fps) { i = track.nearest(t); track.timeMiss++; }
                return track.at(i);
            };
            var counted = function (rr, cv) {
                if (rr && rr.teethPx > 0) prog.teethFrames++;
                if (st.spots.length && rr && rr.spotsHealed === st.spots.length) prog.spotFrames++;
                prog.done++;
                // Показывать, что именно сейчас обрабатывается: полоса без
                // картинки читается как «оно там что-то делает».
                if (prog.done % 5 === 1) { try { pvA.drawImage(cv, 0, 0, st.outW, st.outH); } catch (_) { } }
            };
            return P.run({
                slug: SLUG, file: st.file, params: {}, crf: 21,
                trimStart: st.inT, trimEnd: st.outT,
                geometry2d: {
                    width: ew, height: eh,
                    // Окно: по два кадра на воркер — пока один считается,
                    // следующий уже ждёт в его очереди.
                    concurrency: pl ? Math.min(8, pl.size * 2) : 1,
                    draw: function (gctx, drawVideo, cw, ch, idx, t) {
                        drawVideo(0, 0, W, H, 0, 0, cw, ch);
                        if (innerErr) return;
                        var g = at(t);
                        if (pl) {
                            return pl.render(gctx, g.faces, cw, ch, pr, { k: g.k, presmoothed: true })
                                .then(function (rr) { counted(rr, gctx.canvas); });
                        }
                        var rr;
                        try { rr = proc.render(gctx, g.faces, cw, ch, pr, { k: g.k, presmoothed: true }); }
                        catch (e3) { innerErr = e3; return; }
                        counted(rr, gctx.canvas);
                    },
                },
                onProgress: function (p) { hwProgress(prog, t0, 'render', p); },
                abortSignal: st.abort.signal,
            });
        }
        prog.renderMs = Math.round(workNow() - prog.renderT0);
        if (innerErr) throw fail(innerErr, 'render-frame');
        if (!res || !res.blob || res.blob.size < 1000) throw fail(new Error('hw-empty-output'), stage);
        // Исходник со звуком, а в выдаче звука нет — так отдавать нельзя;
        // покадровый проход несёт свою дорожку.
        if (res.audioExpected > 0 && res.audioMuxed === 0) throw fail(new Error('hw-audio-dropped'), stage);
        return { blob: res.blob, track: track };
    }

    /**
     * Сколько воркеров под экспорт (0/1 — считать на главном потоке, как раньше).
     *
     * Ядра — минус два (главный поток держит декодер, кодер и чтение кадров;
     * ещё одно — браузеру), не больше четырёх. Замер настоящего экспорта
     * 26.09.2026 (1080p, 240 кадров, i9-13980HX): рисование 101.6 с на главном
     * потоке → 46.8 с на двух воркерах, 40.6 на трёх, 32.1 на четырёх.
     *
     * И память: тот же замер — пик всего куста Chrome +823 МБ с четырьмя
     * воркерами против +240 МБ без них, ~145 МБ на воркер при лице в 20 %
     * кадра; крупный план вдвое больше, отсюда 175 байт на пиксель кадра на
     * воркер. Бюджет — пятая часть памяти устройства на десктопе и шестнадцатая
     * на телефоне (телефон убивает вкладку — и с ней экспорт — задолго до
     * того, как десктоп упрётся в память), и на телефоне не больше двух.
     * Сколько памяти у устройства — решает window.__memBudget, единственный
     * владелец этого числа: у iOS (любой браузер там — WKWebView) потолок
     * вкладки фиксирован и низок — constrained(), пула нет, прежний путь; где
     * браузер память не сообщил (десктопные Firefox и Safari — assumed()),
     * воркеров не больше двух: ставить вкладку на догадку нельзя.
     */
    function poolSize(ew, eh) {
        if (!window.Worker || !CFG.retouchWorker || !window.FRVideo || !window.FRVideo.createPool) return 0;
        if (typeof window.__vrPool === 'number') return window.__vrPool;     // отладка и стенды
        var B = window.__memBudget;
        if (!B || B.constrained()) return 0;
        var cores = navigator.hardwareConcurrency || 2;
        var n = Math.max(0, Math.min(4, cores - 2));
        var mobile = !!(navigator.userAgentData && navigator.userAgentData.mobile) ||
            /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
        if (mobile || B.assumed()) n = Math.min(n, 2);
        var budgetMb = B.gb() * 1024 / (mobile ? 16 : 5);
        var perWorkerMb = ew * eh * 175 / 1048576;
        var byMem = Math.floor(budgetMb / Math.max(1, perWorkerMb));
        return Math.max(0, Math.min(n, byMem));
    }

    /** Покадровая перемотка <video> + CanvasVideoExport (запасной путь). */
    async function exportSeek(ew, eh, fps, container, pr, span, prog, t0) {
        if (!window.CanvasVideoExport) throw new Error('exporter-missing');
        var sup = await window.CanvasVideoExport.isSupported({ width: ew, height: eh, fps: fps, format: container });
        if (!sup || !sup.ok) throw new Error('unsupported:' + (sup && sup.reason));

        // Звук исходника переносим как есть — ретушь его не касается.
        // Фрагмент режем и по звуку: иначе выдача будет короче дорожки.
        var audioBuffer = null;
        try { audioBuffer = await decodeAudio(st.file, st.inT, st.outT, container); } catch (_) { audioBuffer = null; }

        var proc = window.FRVideo.createProcessor(st.scene, {});
        var work = document.createElement('canvas');
        work.width = ew; work.height = eh;
        var wctx = work.getContext('2d', { willReadFrequently: true });
        var pvA = el.after.getContext('2d');

        return window.CanvasVideoExport.encode({
            width: ew, height: eh, fps: fps, durationSec: span,
            format: container, qualityCrf: 21, audioBuffer: audioBuffer,
            signal: st.abort.signal,
            drawFrame: async function (ctx, idx) {
                // Середина кадра, а не его начало: на границе браузер отдаёт
                // ПРЕДЫДУЩИЙ кадр (замер 06.09 на 23.976 к/с — на каждом кадре).
                await seek(st.inT + (idx + 0.5) / fps);
                var rr = await proc.frame(el.video, wctx, ew, eh, pr);
                if (rr && rr.teethPx > 0) prog.teethFrames++;
                if (st.spots.length && rr && rr.spotsHealed === st.spots.length) prog.spotFrames++;
                ctx.drawImage(work, 0, 0);
                prog.done++;
                if (prog.done % 5 === 1) { try { pvA.drawImage(work, 0, 0, st.outW, st.outH); } catch (_) { } }
            },
            onProgress: function (f) {
                var elapsed = (workNow() - t0) / 1000;
                var eta = f > 0.02 ? Math.max(0, elapsed / f - elapsed) : 0;
                setProgress(true,
                    (I.frames_done || '').replace(':done', String(prog.done)).replace(':total', String(prog.total)),
                    f, eta ? (I.eta || '').replace(':t', human(eta)) : '');
            },
        });
    }

    /**
     * ЗВУК ДЛИННОГО ИСХОДНИКА — САМОЕ ДОРОГОЕ, ЧТО ЕСТЬ В ЭКСПОРТЕ.
     *
     * `decodeAudioData` умеет только «весь файл целиком»: диапазона у него нет.
     * Значит, вырезая двадцать секунд из сорокаминутного вебинара — то есть
     * делая ровно то, ради чего в туле появились ручки фрагмента, — мы
     * разжимали ВЕСЬ звук. Замерено 05.09 на десятиминутном моно-ролике:
     * 110 МБ PCM и +173 МБ к рабочему набору браузера. Сорок минут стерео
     * дают 921 МБ — на телефоне с потолком вкладки 500–700 МБ это смерть
     * вкладки ещё до первого обработанного кадра.
     *
     * Лечение измерено, а не придумано: `decodeAudioData` РЕСЕМПЛИТ результат
     * в частоту контекста, и понижение режет память ровно во столько же раз
     * (замер: 48 кГц → 110 МБ, 24 кГц → 55 МБ, 16 кГц → 37 МБ, длина буфера
     * пропорциональна). Для говорящей головы 24 кГц неотличимы на слух, 16 —
     * разборчивы. Поэтому лестница: полная частота, потом пониженная, потом
     * честный отказ от звука — и о каждом шаге СКАЗАНО ДО нажатия.
     *
     * ⛔ Не «просто декодировать поменьше»: бюджет считается от памяти
     * устройства, а не от вкуса.
     */
    var AUDIO_RATES = [0, 24000, 16000];   // 0 — родная частота контекста

    /** Сколько мегабайт PCM даст ЭТОТ исходник на заданной частоте. */
    function audioPcmMB(durSec, rate) {
        // Худший правдоподобный случай: стерео, float32. Каналы и частоту
        // источника до декодирования не узнать, а ошибиться безопаснее в
        // сторону «дороже».
        return durSec * (rate || 48000) * 2 * 4 / 1048576;
    }

    function audioBudgetMB() {
        // Память устройства — от владельца числа (window.__memBudget): у iOS это
        // бюджет ВКЛАДКИ, а не ОЗУ телефона.
        var dm = window.__memBudget ? window.__memBudget.gb() : 2;
        var b = dm * 60;
        return b < 120 ? 120 : (b > 480 ? 480 : b);
    }

    /** Какую частоту потянет этот исходник: 0 — родную, null — звука не будет. */
    function audioPlan(durSec) {
        var budget = audioBudgetMB();
        for (var i = 0; i < AUDIO_RATES.length; i++) {
            if (audioPcmMB(durSec, AUDIO_RATES[i]) <= budget) return AUDIO_RATES[i];
        }
        return null;
    }

    /**
     * Частоты, которые ПРИНИМАЕТ кодировщик контейнера. Замер 05.09.2026,
     * Chrome 152, `AudioEncoder.isConfigSupported`:
     *
     *     mp4a.40.2 (AAC): 8000 нет · 16000 нет · 24000 нет · 32000 нет
     *                      44100 ДА · 48000 ДА
     *     opus:            8000…48000 — все ДА
     *
     * Это поймал прибор, а не рассуждение: первая версия лестницы отдавала
     * энкодеру буфер 24 кГц, тот молча закрывался, и весь экспорт падал
     * «Cannot call encode on a closed codec». То есть «экономия памяти»
     * ломала выдачу вместо того, чтобы её спасти.
     */
    function encoderTakesRate(container, rate) {
        if (container === 'webm') return true;              // Opus берёт любую
        return rate === 44100 || rate === 48000;            // AAC — только эти
    }

    /** Поднять частоту КОРОТКОГО фрагмента (не всего файла). */
    async function resampleTo(buf, rate) {
        var OC = window.OfflineAudioContext || window.webkitOfflineAudioContext;
        if (!OC) return buf;
        var len = Math.max(1, Math.ceil(buf.duration * rate));
        var oc = new OC(buf.numberOfChannels, len, rate);
        var src = oc.createBufferSource();
        src.buffer = buf;
        src.connect(oc.destination);
        src.start(0);
        return await oc.startRendering();
    }

    /**
     * Звук исходника → AudioBuffer нужного ФРАГМЕНТА.
     *
     * ⚠️⚠️ Декодируем через OfflineAudioContext, а не через AudioContext, и это
     * замер, а не вкус (06.09.2026). У живого контекста `decodeAudioData` на
     * ролике БЕЗ звуковой дорожки отвечает отказом через **15 с** в обычном
     * Chrome и через 49 с в headless — контекст привязан к звуковому
     * устройству, и отказ приходит по таймауту его очереди. Offline-контекст
     * тот же декодер отдаёт за 0.5 мс. Это было 49 из 76 с всего экспорта на
     * стенде и 15 с чистого ожидания у человека с немым роликом — до первого
     * обработанного кадра, при полосе «Экспорт…» без движения.
     * Ресемплинг в частоту контекста у Offline тот же (это свойство
     * decodeAudioData, а не типа контекста), поэтому лестница частот ниже
     * работает как прежде; «родная» частота = 48 000, её кодировщик берёт.
     */
    async function decodeAudio(file, from, to, container) {
        var OC = window.OfflineAudioContext || window.webkitOfflineAudioContext;
        if (!OC) return null;
        var rate = audioPlan(st.dur);
        if (rate === null) return null;         // сказано в строке плана
        var buf = await file.arrayBuffer();
        var ctx = new OC(1, 1, rate || 48000);
        var out;
        try {
            var full = await ctx.decodeAudioData(buf);
            if (from <= 0.001 && to >= full.duration - 0.001) {
                out = full;
            } else {
                var sr = full.sampleRate;
                var a = Math.max(0, Math.floor(from * sr));
                var b = Math.min(full.length, Math.ceil(to * sr));
                var len = Math.max(1, b - a);
                out = ctx.createBuffer(full.numberOfChannels, len, sr);
                for (var c = 0; c < full.numberOfChannels; c++) {
                    out.getChannelData(c).set(full.getChannelData(c).subarray(a, b));
                }
            }
        } finally {
            try { ctx.close && ctx.close(); } catch (_) { }   // у Offline-контекста close() нет
        }
        // Поднимаем ТОЛЬКО фрагмент: он короткий, и памяти это стоит копейки,
        // тогда как поднять весь файл значило бы вернуть ту самую беду, от
        // которой пониженная частота и спасала.
        if (!encoderTakesRate(container, out.sampleRate)) out = await resampleTo(out, 48000);
        return out;
    }

    function outputName(container) {
        var ext = container === 'webm' ? 'webm' : 'mp4';
        try {
            if (window.VideoFormats && window.VideoFormats.ext) ext = window.VideoFormats.ext(container) || ext;
        } catch (_) { }
        var base = (st.file && st.file.name || 'video').replace(/\.[^.]+$/, '');
        if (window.__outputName) {
            try {
                // join(base, ext) принимает СТРОКИ. Массив и объект давали
                // «имя,retouch.[object Object]» — файл без расширения, который
                // система не свяжет ни с одной программой.
                return window.__outputName.join(base + '-retouch', ext);
            } catch (_) { }
        }
        return base.replace(/[\\/:*?"<>|]+/g, '_') + '-retouch.' + ext;
    }
})();
